import { advanceStairClimb, clearStairTravel } from "./stair_travel"
import type { PreSpawnEffect, PreSpawnEffectView } from "./pre_spawn_effect"
import { bindContent, contentCatalog } from "./content/catalog"
import type { RuntimeCatalog } from "./content/schema"
import { resolveEngineValues } from "./content/engine_values"
import type { ChargedWeaponDefinition, WeaponChargeView, AreaProjectileView, ChargedWeaponAction } from "./charged_weapon"
import { Eyes, Nose, Weapon as Weapons } from "game-common/character/character"
import { getCurrentTime, setGlobalTimeFunction } from "./time_util"
import type { AnimationPoseView } from "./animation_pose"
import type { AttackWindupView } from "./charged_weapon"
import { BonecallerAttack, BonecallerRitual } from "./bonecaller_combat"

import { Color, Hair, Race, Sex } from "./character/character"
import { CollisionResultPool } from "./collision_result_pool"
import { CoordinatePool } from "./coordinate_pool"
import { ItemTypeMeta } from "./item/item"
import { EntityTile, MapDelta } from "./map/map"
import { Mechanics } from "./mechanics/mechanics"
import { DayPhase } from "./mechanics/physics_mechanics"
import { VectorLine } from "./ray_casting"
import { Skill } from "./skills/skills"
import { Faction } from "./social/faction"
import {
    blankCoordinate,
    clamp,
    emptyCoordinate,
    fillCoordinate,
    getAngle,
    isDiagonal,
    radiansToCardinalDirection,
    randomIntBetweenRng,
} from "./util"
import { MinmapConfig, TileMapMetaProvider } from "./tile_map_meta_provider"
import { moveAgainstTileProfiles } from "./tile_blocking_mover"
import type { TileBlockingRect } from "./tile_blocking_geometry"
import { Effect } from "./mechanics/effect_mechanics"
import { Ammo } from "./item/item_type"

export const TileSize: number = 32

export type RunLevel = "prod" | "dev"

// Movement Inertia Configuration
export const MovementInertiaConfig = {
    ENABLED: true,
    FRICTION: 0.815, // Friction coefficient (0-1) - higher = less friction (30% more distance)
    ACCELERATION: 0.4, // How fast to reach target (0-1)
    MIN_VELOCITY_THRESHOLD: 0.01, // Minimum velocity to consider as moving
    UPDATE_FPS: 60, // FPS for velocity updates
}

export type Direction = "Up" | "Down" | "Left" | "Right" | "DownRight" | "DownLeft" | "UpRight" | "UpLeft"

export const BasicDirections: Direction[] = ["Up", "Down", "Left", "Right"]

export const Directions: Direction[] = [...BasicDirections, "DownRight", "DownLeft", "UpRight", "UpLeft"]

export type EntityId = string

export type EntityType = "player" | "bullet" | "npc" | "bomb" | "system"

export type NpcType =
    | "outpost"
    | "zombie"
    | "flag"
    | "goal"
    | "door"
    | "lock"
    | "sign"
    | "bomb"
    | "emp"
    | "health"
    | "mana"
    | "spawner"
    | "fire"
    | "light"
    | "collectable"
    | "marker"
    | "campfire"

export type SeekerType = "brute" | "astar"

export type WeaponType = "bomb" | "emp" | "none" | Weapons

export interface WeaponMeta {
    bulletSpeed?: number
    firingLatchMs?: number
    manaCost?: number
    maxDistance?: number
    attackRange?: number
    maxParticles?: number
    maxEntitiesHit?: number
    duration?: number
    equipOrder?: number
    muzzleFireOffset?: number
    slotVisible?: boolean
    baseDamage?: number
    bulletSpriteId?: string
    pushStrength?: number
    bulletType?: Ammo
    collisionDimensions?: Dimensions
}

export interface AmmoMeta {
    effects?: Effect[]
    spriteId?: string
    createNpcProps?: Partial<CreateNpcProps>
}

export interface AbilityMeta {
    equipOrder: number
    shortcutKey: string
    maxCarried?: number
}

const weaponCharacteristicsFor = (catalog: RuntimeCatalog): Record<WeaponType, WeaponMeta> => {
    const base = resolveEngineValues<Record<string, WeaponMeta>>(catalog.source.profiles.weapons, {
        tileSize: { minimum: TileSize }, speed: { minimum: 0 }, hp: { minimum: 0 }, hostility: { minimum: 0 },
    })
    return {
        ...base,
        ...Object.fromEntries(Object.entries(catalog.weapons).map(([weapon, template]) => [weapon, { ...base[template], ...base[weapon] }])),
    }
}
export let WeaponCharacteristics: Record<WeaponType, WeaponMeta> = weaponCharacteristicsFor(contentCatalog)
bindContent(weaponCharacteristicsFor, next => { WeaponCharacteristics = next })

export const AbilityCharacteristics: Record<AbilityType, AbilityMeta> = {
    bomb: {
        equipOrder: 0,
        shortcutKey: "b",
        maxCarried: 20,
    },
    heal: {
        equipOrder: 1,
        shortcutKey: "q",
        maxCarried: 3,
    },
    mana: {
        equipOrder: 2,
        shortcutKey: "e",
        maxCarried: 2,
    },
    speed: {
        equipOrder: 3,
        shortcutKey: "r",
    },
}

export type LeaderboardType = string

export interface LeaderboardMeta {
    items: LeaderboardMetaItem[]
    name: LeaderboardType
    ts: number
}

export interface LeaderboardMetaItem {
    name: string
    count: number
}

export interface ClientMeta {
    showRoomName: boolean
}

export const DefaultClientMeta: ClientMeta = {
    showRoomName: false,
}

export const EntityTypes: EntityType[] = ["player", "bullet", "npc", "bomb"]

export const EntityTypesPlusSystem: EntityType[] = [...EntityTypes, "system"]

export const ScreenDimensions: Dimensions = {
    w: 640,
    h: 360,
}

export const EntityDimensions: Record<EntityType, Dimensions> = {
    player: {
        w: 25,
        h: 25,
    },
    npc: {
        w: 25,
        h: 25,
    },
    bullet: {
        w: 15,
        h: 15,
    },
    bomb: {
        w: 20,
        h: 20,
    },
    system: {
        w: 0,
        h: 0,
    },
}

export type Orientation = "north" | "south" | "east" | "west"

export type EntityState = "live" | "dead"

export type BiomeColorMap = TileMapBase<number>

/** A read-only camera subscription; absent room means local, gone, or unauthorized. */
export interface FocusObservation {
    entityId: EntityId
    room?: SceneRoomState
    visibility?: Visibility
}

export interface WorldState {
    /**
     * A change of level to a storey this client already draws, sent without its map (or the map of
     * the storey just left). The client takes the active map from its passive floor, and asks for a
     * whole room (type_requestRoomSnapshot) if it has none. Only for clients that declared
     * levelHandOff (type_clientCapabilities).
     */
    levelHandOff?: boolean
    observation?: FocusObservation | null
    sceneRooms?: SceneRoomState[]
    /** Active floor links changed without replacing the current room. */
    twoStoreyHouse?: import("./room/ember/two_storey_house").TwoStoreyHouse
    messages: Message[]
    /**
     * Server clock reading at the instant these positions were sampled (the envelope's recv_ts,
     * stamped by ClientProxy.sendWorldState). The client needs it to interpolate: a sample's own
     * arrival or processing time on the client carries network jitter and frame quantization,
     * and interpolating against those renders a constant-speed walk as a varying-speed one.
     * Filled in by the client from the envelope on receipt, not sent inside the payload.
     */
    serverTs?: number
    scores?: Record<EntityId, number>
    tilemaps?: TileMapDirectory
    worldMetaDimensions?: WorldMetaDimensions
    tilemapMeta?: Tileset[]
    minimapConfig?: MinmapConfig
    visibility?: Visibility
    leaderboards?: LeaderboardMeta[]
    roomId?: EntityId
    roomName?: string
    dayPct?: number
    dayPhase?: DayPhase
    biomeColorMap?: BiomeColorMap
    locationIcons?: LocationIcon[]
    playerLocations?: Record<EntityId, Location>
    runLevel: RunLevel
}

/** Passive companion-room snapshots. These entities never enter active-room prediction. */
export interface SceneRoomState {
    serverTs?: number
    roomId: EntityId
    tilemaps?: TileMapDirectory
    tilemapMeta?: Tileset[]
    mapDeltas?: import("./map/map").MapDelta[]
    messages: Message[]
}

export interface Coordinate {
    x: number
    y: number
}

export interface MapCoordinateReport extends Coordinate {
    intersectionType: MapIntersectionType
}

export class NamedCoordinate {
    id: string
    coordinate: Coordinate
    constructor(id: string, x: number, y: number) {
        this.id = id
        this.coordinate = { x, y }
    }
}

export interface Location extends Coordinate {
    roomId: string
    quadrantId?: string
    context?: any
}

export interface BiomeMapPoint {
    biomeCoordinate: Coordinate
    color: number
}

export interface Dimensions {
    w: number
    h: number
}

export interface NamedLocation {
    locationId: string
    roomId: string
    context?: any
}

export interface TeleportPipe {
    id?: EntityId
    a: Location
    b: NamedLocation
    dimensions: Dimensions
    exitOrientation?: Orientation
}

export interface Region extends NamedLocation, Location {
    bounds: Bounds
}

export interface LocationIcon extends Location {
    id?: string
    icon: string
    initialMapFeature?: boolean
    noProximitySpawn?: boolean
    name?: string
}

export interface GeographicFeatureLocation extends Location {
    id: string
    name: string
}

export interface MinimapMeta {
    points: BiomeMapPoint[]
    icons: LocationIcon[]
    clear?: boolean
}

export interface WorldMapPlayerLocation {
    location: Coordinate
    isViewer: boolean
    label: string
}

export interface WorldMapMeta {
    map: MinimapMeta
    dimensions?: Dimensions
    playerLocations: WorldMapPlayerLocation[]
    locationIcons: LocationIcon[]
    questMarkers: LocationIcon[]
    geographicFeatureLocations: GeographicFeatureLocation[]
}

export interface Path {
    id: string
    locations: Location[]
}

export interface Sign {
    location: Location
    label1: string
    flags?: {}
}

export interface ItemDescriptor {
    itemTypeMeta: ItemTypeMeta
    quantity?: number
}

export interface InventoryItem {
    id: string
    itemType: WeaponType
    itemTypeMeta?: ItemTypeMeta
    description?: string
    active?: boolean
    readied?: boolean
    weight?: number
    flags?: any
    quantity?: number
    hp?: number
}

export interface AbilityItem {
    abilityType: AbilityType
    quantity: number
}

export interface Inventory {
    items: InventoryItem[]
    abilities: Record<string, AbilityItem>
    lastUpdateTs: number
}

export interface OutpostMeta {
    fame: number
    skills: Skill[]
    defenders: number
}

export interface StairTravelView {
    /** One physical flight, projected independently into its two simulation rooms. */
    /** rise maps the ladder to its hatch, independently of building floor spacing. */
    flight?: { lowerRoomId: string; upperRoomId: string; base: Coordinate; rise: number;
        total: number; from: number; to: number; surface: boolean;
        /** Bare stacked shaft: one uninterrupted climb, without a floor-opening animation. */
        continuous?: boolean }
    projection?: boolean
    /** Physical player represented by a room-local visual projection. */
    sourceEntityId?: string
    /** Player-controlled timeline; absent for automatic NPC travel. */
    climb?: { elapsedMs: number; moving: boolean; direction?: Direction; reverse?: boolean; inputSequenceNumber?: number }
    /** The floor beneath this flight, used when the player jumps off it. */
    lowerLanding?: Location
    id: string
    roomId: string
    direction: "up" | "down"
    durationMs: number
    remainingMs: number
    phase: "depart" | "arrive"
    start: Coordinate
    end: Coordinate
    walkMs: number
    transitionMs: number
    /** Local base/top of the stair flight or edges of a downward opening. */
    ramp?: { entry: Coordinate; top: Coordinate }
    exitDirection: Direction
    exitAngle: number
}

export interface FloorFallView {
    /** A short stair dismount permits horizontal movement during the drop. */
    stairJump?: boolean
    /** The drop started at an exposed floor edge, rather than an interior opening. */
    exterior?: boolean
    /** Room in which the incoming fall animation belongs. */
    roomId?: string
    /** A transfer into this room starts above the viewport. */
    fromAbove?: boolean
    arrivalDurationMs?: number
    id: string
    phase: "falling" | "arriving" | "landed"
    durationMs: number
    remainingMs: number
    distance: number
    /** Number of levels actually crossed during this fall. */
    levelsFallen?: number
    /** Time spent down after landing; overrides the levels-fallen rule (a climber knocked off a ladder). */
    recoveryMs?: number
    /** A chained drop retains gravity and cumulative time across room transfers. */
    trajectory?: { gravity: number; distanceBefore: number; endElapsedMs: number; totalDistance?: number }
}

export interface CrossFloorAim extends Coordinate { roomId: string; surface?: boolean }

export interface Message {
    strikeable?: boolean
    crossFloorAim?: CrossFloorAim
    crossFloorProjectile?: boolean
    /** Visual aiming point in the firing room's coordinates, including cross-floor projection. */
    projectileTarget?: Coordinate
    landedArrow?: boolean
    chargedWeaponAction?: ChargedWeaponAction
    interest?: "enter" | "update" | "leave"
    entityType?: EntityType
    npcType?: NpcType
    spriteId?: string
    noGlint?: boolean
    entityState?: EntityState
    press_time?: number
    direction?: Direction
    angle?: number
    input_sequence_number?: number
    entityId?: EntityId
    entityIds?: EntityId[]
    ownerEntityId?: EntityId
    position?: number[]
    last_processed_input?: number
    origin?: Coordinate
    speed?: number
    maxSpeed?: number
    speed2?: Coordinate
    creationTs?: number
    maxDistance?: number
    name?: string
    color?: number
    visible?: boolean
    hunger?: number
    maxHunger?: number
    hp?: number
    hpMax?: number
    mana?: number
    roomId?: EntityId
    canShootBullets?: boolean
    canLayBomb?: boolean
    accessToken?: string
    leaderboards?: LeaderboardMeta[]
    joinedTeam?: boolean
    score?: number
    scoreTillNextLevel?: number
    level?: number
    weapon?: Weapon
    movement?: Movement
    item?: string
    shortcutKey?: string
    entities?: Message[]
    paralyzeUntilTs?: number
    // True while this entity is DOWNED -- see Entity.downedUntilTs -- or a living lord is below
    // 20% health. Sent as the server's verdict
    // rather than as the deadline behind it, so a client whose clock disagrees with the server's
    // still frees the player at exactly the moment the server does.
    downed?: boolean
    // True while this player is RUNNING (Entity.boosted: the run key held and affordable), so
    // every client can draw the run gait rather than the walk. The server's verdict, sent beside
    // `downed` for the same reason: a body that only ran on detailed messages would flicker.
    boosted?: boolean
    // True while this entity is a player on an INVASION -- visiting somebody else's camp as one of
    // its wave attackers (see INVASION_FLAG). The client has no notion of flags, so this is how it
    // knows to offer "Let go". Sent for the invader's own entity; a verdict, like `downed`.
    invading?: boolean
    // The pose an ember rescue survivor is drawn in: "downed" is face-down at the wreck, waiting to
    // be helped up, "following" the tilted limp of an escort under way. Carried as its own field
    // for the same reason as `downed` and `invading` -- the server keeps the truth on flags
    // (flags.rescueState), and updateEntityFromMessage wipes every flag a client receives.
    //
    // "downed" is the same held pose Message.downed asks for, reached by a different road: a
    // fallen PLAYER is a deadline on the server, an injured survivor is a phase of the rescue. The
    // client only cares that the body is on the ground (see LpcComposite.updateHeldPose), so the
    // hold-to-help-up finishing is what plays her get-up -- the collapse run backwards.
    rescuePose?: "downed" | "following"
    // Whether to PROMPT whoever can see her to hold on her and get her to her feet. Beside
    // `rescuePose` and carried for the same reason: the truth is a flag (flags.rescueHelpHint) and
    // no flag ever reaches a client.
    //
    // It is the server's call rather than something the client could work out from the interaction
    // list, because the hold is REFUSED while a skeleton is still standing over the wreck (see
    // RescueDirector.canHelp) -- a prompt during that fight would be telling the player to do the
    // one thing the encounter will not let them do yet.
    rescueHelpHint?: boolean
    chargedWeaponDefinition?: ChargedWeaponDefinition
    weaponCharge?: WeaponChargeView
    visualEffects?: Effect[]
    floorFall?: FloorFallView
    stairTravel?: StairTravelView
    animationPose?: AnimationPoseView
    attackWindup?: AttackWindupView
    areaProjectile?: AreaProjectileView
    bonecallerAttack?: BonecallerAttack
    bonecallerCombatRadius?: number
    bonecallerRitual?: BonecallerRitual
    preSpawnEffectView?: PreSpawnEffectView
    bonecallerEmergenceRemainingMs?: number
    // How long is left on a HELD interaction being performed on this entity, and how long that hold
    // runs in full -- the countdown dial the client draws over it (see gui/hold_timer.ts). Absent
    // unless a hold is actually under way.
    //
    // It comes from the server for the same reason the hold's timing does (see HeldInteraction): a
    // dial run off the client's own clock would keep filling after the server had refused the hold
    // outright, or dropped it for a lapsed lease, and so promise an outcome that was never coming.
    // Sent as a REMAINING time rather than as the deadline behind it, like `downed`, so no client
    // clock has to agree with the server's.
    holdRemainingMs?: number
    holdDurationMs?: number
    // Public context-menu onboarding state; server-only entity flags are not transmitted.
    rainDamaged?: boolean
    contextMenuSubject?: "campfire" | "recruit"
    campfireMenuLearned?: boolean
    recruitMenuLearned?: boolean

    lastUpdateTs?: number
    inventory?: Inventory
    upgradePoints?: number
    appearance?: Appearance
    clickable?: boolean
    complete?: boolean
    expirationTs?: number
    quantity?: number
    lightMeta?: LightMeta
    spriteScale?: number
    leaderEntityId?: EntityId
    sigilIcon?: string
    dimensions?: Dimensions
    debug?: boolean
    afk?: boolean
    active?: boolean
    faction?: Faction
}

// One clickable entry in the ember-room HUD roster strip. Carries appearance + inventory so
// the client can render the same front-facing "paperdoll" portrait used in the inventory view
// (see EntityDisplayer / LpcLayered), for notable NPCs regardless of how far they are from the
// player. Sent PER VIEWER via Protocol.type_updateEmberRoster -- per viewer rather than per room
// because of "player" below: a card for yourself would be a card for the body the camera is
// already centred on.
//
// "player" is another PERSON in the camp, invited in off the who-else-is-playing feed. They are on
// the strip for the reason everyone else is -- the strip answers "where is everyone and how are
// they doing", and a partner is the one answer you most need -- but they are not crew: their card
// opens the follow cam rather than the command wheel, because a person is not ordered about.
// "visitor" is a recruit standing at the fire asking to be fed: not crew, not a person playing,
// but the one thing on this strip you are being asked to answer. Its card is drawn DETACHED from
// the household (see EmberRosterWidget), which is why it needs a role of its own rather than being
// folded in once it is hired -- the moment it stops being a visitor is the moment it joins, and the
// strip animates exactly that.
export type EmberRosterRole = "lord" | "player" | "defender" | "forager" | "visitor"

// WHAT ONE CAMP MEMBER IS DOING, as the single word their roster card shows.
//
// The strip answers "where is everyone and how are they doing"; this is the half of that question a
// portrait and two bars could never answer -- whether somebody is working, waiting on an order, or
// about to walk out. ONE value per member, never a list: a card is 70px wide, and a member in three
// states at once (hungry, stuck, under orders) needs the player told the most pressing one rather
// than all of them. The ladder that picks it is campaigns/ember/member_status.ts, which is where the
// precedence is written down and tested; the client only maps the answer to a label and a colour.
//
//   awaitingOrders  stuck: it cannot reach where it was sent and will do nothing until ordered again
//   needsFood       at the chest, nothing in it, the dismissal clock running
//   hungry          off its post, walking to the chest
//   idle            no standing order at all
//   fighting        engaging something, on its own initiative (a posted or guarding defender)
//   holding         on the spot a "Hold Position" order named
//   defending       in the Lord's squad, guarding the camp
//   following       in a player's squad
//   foraging        out chopping wood
//   feedingFire     ferrying wood to the campfire and feeding it
//   hauling         carrying a load back to the fire
//   wantsFood       VISITOR: a stranger at the fire asking to be paid in food
//   needsJob        VISITOR: paid, and waiting to be given a role
export type EmberMemberStatus =
    | "awaitingOrders"
    | "needsFood"
    | "hungry"
    | "idle"
    | "fighting"
    | "holding"
    | "defending"
    | "following"
    | "foraging"
    | "feedingFire"
    | "repairing"
    | "hauling"
    | "wantsFood"
    | "needsJob"

export interface EmberRosterEntry {
    /** Authoritative camp location, including when the member is outside the active room. */
    roomId?: EntityId
    floor?: number
    crossRoom?: boolean
    canKick?: boolean
    playMode?: EmberPlayMode
    entityId: EntityId
    role: EmberRosterRole
    name?: string
    hp?: number
    hpMax?: number
    appearance?: Appearance
    inventory?: Inventory
    // Campfire strength (0..1), only meaningful for the lord — drives an animated fire-particle
    // backdrop behind the lord's portrait (weaker fire => dimmer, sparser).
    fireStrength?: number
    // How full the member is (1 = just ate, 0 = about to give up and walk out), drawn as a second
    // bar under the HP bar. Only recruits have a hunger arc — the lord's card has no hunger bar, so
    // this is undefined for him. Computed by the recruitment manager, which owns the arc's clocks,
    // and read off the member as flags.hungerFullness.
    hunger?: number
    // How much of a VISITOR's patience is left (1 = just arrived, 0 = about to walk back out),
    // drawn as a draining ring on their card. Undefined for everyone else: a member of the camp is
    // not on a clock. The two clocks it covers (waiting to be fed, then waiting to be given a job)
    // both live on the recruit as flags, and the roster campaign reads whichever one is running.
    patience?: number
    // A visitor that has been paid. Its card stops asking and starts offering: the "joining" dots
    // quicken, and the role picker becomes reachable.
    fed?: boolean
    // The one thing this member is most pressingly doing or waiting for, drawn as a pill above their
    // card. Computed on the server (memberStatus) because every input is a flag or a squad
    // membership the client cannot see. Undefined for the lord -- his pill is the fire's -- and for
    // another player, who is not under anybody's command.
    status?: EmberMemberStatus
}

// One dot on the ember-room minimap, in FINE (pixel) room coordinates. Deliberately positions
// only: the map is a glance at where everyone is, and anything richer already has the roster card.
//
// `id` is the entity's own id, and it is what makes the once-a-second broadcast affordable: the
// client matches a dot to the one it drew a second ago and GLIDES it to the new position instead of
// teleporting it, so a cheap feed still reads as motion.
export interface EmberMinimapDot {
    id: EntityId
    x: number
    y: number
}

// Per-room snapshot driving the ember minimap (Protocol.type_updateEmberMinimap). Positions are
// authoritative and sent for EVERY camp member and attacker in the room, however far from the
// player they are -- which is the point of the map, and why it can't be derived on the client from
// the world-state sync (that only refreshes a distant entity about once a second).
//
// `width`/`height` are the room's extent in the same fine units as the dots, so the client can
// scale them into the widget without knowing anything about the room. Sent PER VIEWER, because
// `players` below must leave the viewer out: the client draws its OWN dot from its own entity,
// which it already tracks per frame, and a second dot on top of it would just smear.
// Row-major, least-significant-bit first; base64 carries packed bytes through JSON.
export interface EmberMinimapExploration {
    cellTiles: number
    columns: number
    rows: number
    revision: number
    bits: string
}

export interface EmberMinimapState {
    roomId?: EntityId
    runId?: string
    generationId?: string
    terrain?: "camp" | "cave"
    // Omitted when unchanged; identity above still travels with every dot snapshot.
    exploration?: EmberMinimapExploration
    // Dev-only chamber outlines, sent only to viewers who enabled /ember voids. Fine coordinates.
    voidRooms?: { x: number; y: number; width: number; height: number }[]
    width: number
    height: number
    // Camp members (the roster's lord / defenders / foragers / recruits) -- white dots.
    members: EmberMinimapDot[]
    // Wave attackers still standing -- red dots.
    attackers: EmberMinimapDot[]
    // The OTHER people in the camp -- green dots, like the viewer's own, and the same size: they
    // are players, and the map should say so at a glance. Absent from an older server's snapshot,
    // so the client treats a missing list as an empty one.
    players?: EmberMinimapDot[]
    // Strangers at the fire asking to be taken on -- gold dots, and the only ones that PULSE.
    // Nothing else on this map moves except by travelling, so the pulse alone reads as "this one is
    // new" without a legend. Absent from an older server's snapshot; a missing list is an empty one.
    visitors?: EmberMinimapDot[]
    // The same strangers EARLIER: still walking in from the edge of the map towards the fire that
    // drew them. Same gold, no pulse -- somebody is coming, and the pulse is kept for when they
    // arrive and ask. This is the only feed that shows the walk at all (the roster withholds the
    // card until they are asking), which is the whole reason for a separate list rather than an
    // early entry in `visitors`. Absent from an older server's snapshot; a missing list is empty.
    approaching?: EmberMinimapDot[]
    rescue?: EmberMinimapDot & { state: "Waiting" | "Following" | "In danger" }
    mine?: EmberMinimapDot & { explored: boolean }
    bonecaller?: EmberMinimapDot & {
        state: "active" | "interrupted" | "completed"
        stage: 0 | 1 | 2 | 3
        warned: boolean
    }
}

// One other player logged into the server, as the ember camp's "who else is here" panel shows them.
// Sent per-viewer via Protocol.type_updateEmberPlayers, and the list a player receives never
// contains that player -- the panel is about everybody ELSE.
export interface EmberPlayerEntry {
    playMode?: EmberPlayMode
    entityId: EntityId
    name?: string
    // Which wave the camp they are in is currently fighting off, 1-based. Undefined when they are
    // not in an ember camp at all, and 0 when they are in one whose first wave hasn't landed yet --
    // the panel draws those two differently, so they stay distinct on the wire.
    wave?: number
    // Whether they are already standing in the VIEWER's own room, which is the one case where
    // inviting them would do nothing.
    here?: boolean
}

// An outstanding invitation into somebody's ember camp, as the invited player's prompt shows it.
// Sent to that one player via Protocol.type_updateEmberInvite, and answered with
// Protocol.type_answerEmberInvite -- so nobody is moved anywhere without saying yes first.
//
// Reaches a player WHEREVER they are, including a room with no camp in it: the invitation is about
// somewhere else, which is the whole point of it.
export type EmberPlayMode = "pvp" | "cooperation"

export interface EmberInvite {
    playMode?: EmberPlayMode
    // Who is asking, and what to call them on the prompt.
    fromEntityId: EntityId
    fromName?: string
    // How long the prompt has before the server stops honouring an answer. The client drains its own
    // copy of this and takes the prompt down at zero, so the two agree without a second message.
    expiresInMs: number
}

// Per-player config for client-side faction tracking (offscreen edge arrows).
export interface FactionTrackingConfig {
    // Which faction's members to track; null clears/disables tracking.
    faction: Faction | null
    // Whether tracking is active on receipt (the player can still toggle it in the client).
    enabled: boolean
}

// Per-room config for the client-side windblown-leaves ambience: leaves tumbling across the world in
// gusts. Purely cosmetic and entirely client-side -- the server never simulates a leaf, it only says
// whether this room has wind and what it feels like. A room that says nothing gets nothing drawn and
// pays nothing (see Room.windLeaves / the "windLeaves" room property).
export interface WindLeavesConfig {
    // False (or an absent config) draws nothing at all: the client hides the container and skips the
    // whole simulation, so an indoor/still room costs one boolean per frame.
    enabled: boolean
    // Wind heading in DEGREES, in world-compass terms: 0 = due east (+x), 90 = due south (+y).
    // Default is a shallow south-easterly, which reads as "across and slightly down" on screen.
    directionDeg?: number
    // How many leaves are airborne at once in the box around the player. This is a live sprite count
    // on the world surface, so it is the one knob that costs anything -- keep it small for the budget
    // hardware target. Clamped client-side to WindLeaves.MAX_LEAVES.
    count?: number
    // Baseline drift speed along the heading, in world px/sec, before gusts and per-leaf variation.
    speed?: number
    // 0..1: how hard the shared gust envelope swings the baseline speed. 0 is a steady breeze, 1 is
    // surges and lulls. This is what makes a field of leaves read as WIND rather than as snowfall.
    gustiness?: number
    // Leaf tints (0xRRGGBB), sampled per leaf. Pick a seasonal set: greens for summer, ochres/rusts
    // for autumn. Omitted = the client's default autumn mix.
    colors?: number[]
    // Leaf size range as a multiplier on the generated leaf texture (~16px long at 1.0).
    scaleMin?: number
    scaleMax?: number
    // Peak opacity of a leaf at full fade-in. Lower values sit the leaves further back in the scene.
    alpha?: number
    // Whether the wind only blows in daylight (default TRUE). The air goes still after dusk and gets
    // up again with the morning, following the room's own day cycle: the field thins and fades out as
    // the light goes, and stops costing anything at all once it is dark. Set false for a room whose
    // wind should blow at any hour. Ignored by a room with no day cycle, where it is always "day".
    dayOnly?: boolean
}

// Per-room config for the client-side NIGHT FOG: a soft, drifting, parallaxing haze drawn by one
// shader (see the client's NightFog). Like WindLeavesConfig this is cosmetic and simulated entirely on
// the client -- the server only says whether this room has fog and what it should feel like -- and a
// room that says nothing gets nothing drawn and pays nothing.
export interface NightFogConfig {
    // False (or an absent config) draws nothing: no mesh, no noise bake, no shader compile.
    enabled: boolean
    // Peak opacity of the densest part of the bank. This is the "light" in light fog; it is competing
    // with the night darkness for the same contrast, so small numbers go a long way.
    intensity?: number
    // How fast the bank drifts, in world px/sec. Fog wants to be far slower than the leaves.
    speed?: number
    // Drift heading in DEGREES, as for the leaves: 0 = due east (+x), 90 = due south (+y). Matching the
    // room's wind heading is what makes the two ambiences look like the same weather.
    directionDeg?: number
    // Fog colour (0xRRGGBB). A cold blue-grey by default; warm it for smoke, green it for a marsh.
    color?: number
    // Density shaping, and what decides whether this reads as WISPS or as a hazy filter. Noise below
    // `low` is nothing at all, above `high` is solid bank; `low` sits above the field's mean so most of
    // the screen has no fog on it, and the gap between the two is how soft the edges are. Dropping
    // `low` towards zero puts a little fog on every pixel, which is a grey filter however faint.
    low?: number
    high?: number
    // How hard a slower noise field drags the wisps around, in noise-tile units -- the curl that makes
    // them look carried on the air rather than stamped. 0 leaves round blobs.
    warp?: number
    // Whether the fog only forms after dark (default TRUE). It is the exact complement of the leaves'
    // dayOnly gate, so the fog rises as the wind drops. Set false for a room that is always fogged; as
    // with the leaves, a room with no day cycle counts as permanent day and so gets no fog.
    nightOnly?: boolean
}

// Per-room config for the client-side BUTTERFLIES: a few of them wandering over the place by day. Like
// the other two ambiences this is cosmetic and simulated entirely on the client -- see the client's
// Butterflies -- and a room that says nothing gets nothing drawn and pays nothing.
export interface ButterfliesConfig {
    // False (or an absent config) draws nothing: no sprites, no art, no simulation.
    enabled: boolean
    // How many are about. Small numbers: a butterfly is something you notice, and a dozen of them is a
    // plague of moths. Clamped client-side to Butterflies.MAX_BUTTERFLIES.
    count?: number
    // Cruising speed in world px/sec, before each one's own variation and before sheltering from a
    // gust. They do not travel anywhere; this is how briskly they mill about.
    speed?: number
    // Wing tints (0xRRGGBB). Pale colours read against grass; the default mix is mostly whites and
    // yellows with the odd orange.
    colors?: number[]
    // Size range as a multiplier on the generated art (a wingspan of about 10px at 1.0).
    scaleMin?: number
    scaleMax?: number
    // Peak opacity.
    alpha?: number
    // Whether they only fly in daylight (default TRUE), thinning out as the light goes and gone after
    // dark. As with the leaves, a room with no day cycle counts as permanent day.
    dayOnly?: boolean
}

// Per-room config for the client-side FIREFLIES: a few of them hovering over the place after dark.
// Cosmetic and simulated entirely on the client, like the rest of a room's ambience.
export interface FirefliesConfig {
    enabled: boolean
    // How many are about. Only the fraction that happen to be mid-flash are on screen at any moment,
    // so this is larger than the number you see. Clamped client-side to Fireflies.MAX_FIREFLIES.
    count?: number
    // How long one flash LASTS, in seconds. Given as a duration rather than as a share of the cycle,
    // so that how long a glow lasts and how often it comes round are independent -- expressed as a
    // share, changing one silently changes the other, and the length of a flash ends up being the
    // product of two separate random draws and impossible to aim.
    flashSeconds?: number
    // Glow tints (0xRRGGBB). Yellow-greens by default, which is what the real ones are.
    colors?: number[]
    // Size range as a multiplier on the generated glow.
    scaleMin?: number
    scaleMax?: number
    // Peak opacity at the top of a flash.
    alpha?: number
    // Whether they only come out after dark (default TRUE) -- the complement of the butterflies' day
    // gate. As ever, a room with no day cycle counts as permanent day, so it gets none.
    nightOnly?: boolean
}

// Per-room config for the client-side LAVA EMBERS: sparks drifting up off whatever lava is on screen,
// and bubbles swelling and bursting on its surface. Cosmetic and simulated entirely on the client,
// like the rest of a room's ambience -- the server says only that this room has lava worth animating,
// and the client finds the cells itself off the tile types it already holds.
//
// There is no day gate here, unlike the other four. Lava does not care what hour it is, and the only
// room that has any is underground where there is no day cycle at all.
export interface LavaEmbersConfig {
    // False (or an absent config) draws nothing: no sprites, no art, no cell scan, no simulation.
    enabled: boolean
    // Sparks per second, per lava cell ON SCREEN, before the global cap. Per-cell so that a wide pool
    // is livelier than a single cell, and capped so that it cannot be made expensive by being large --
    // see LavaEmbers.MAX_EMBERS / MAX_EMBER_RATE.
    emberRate?: number
    // Bubbles per second across the whole visible pool -- NOT per cell. A bubble is an event you watch,
    // so this is a tempo for the pool rather than a density.
    bubbleRate?: number
    // The cooling ramp a spark is tinted along over its life (0xRRGGBB), hottest first. Interpolated,
    // so two or three stops is plenty; one stop means no cooling at all.
    emberColors?: number[]
    // Bubble tints, sampled per bubble. Deeper and redder than the sparks by default: a bubble is the
    // body of the lava turning over, where a spark is the hot gas coming off it.
    bubbleColors?: number[]
    // Size range as a multiplier on the generated glow (a spark is about 7px across at 1.0).
    scaleMin?: number
    scaleMax?: number
    // Peak opacity of a spark at the top of its rise.
    alpha?: number
}

/**
 * Everything a room says about its own atmosphere, in one object.
 *
 * Each of these is cosmetic and simulated entirely on the client; the server only says whether a room
 * has the thing and what it should feel like. They travel together and are applied together, because
 * they ARE one thing from the player's side -- a camp's weather, not three unrelated animations.
 *
 * They used to travel separately, and the cost of that was a fourth of everything per effect: its own
 * protocol id, campaign event, handler case, proxy method, room field, property parse, client route
 * and client field. Adding a new one now means a key here, a config interface above, and the client
 * effect itself.
 *
 * A room that says nothing gets nothing drawn and pays nothing.
 */
/** A server-authored storm timeline; the client advances elapsedMs on its own monotonic clock. */
export interface RainConfig {
    id: string
    elapsedMs: number
    cloudLeadMs: number
    durationMs: number
    clearing?: { rain: number; cloud: number }
}

export interface RoomAmbience {
    rain?: RainConfig
    // Local tree density narrows outdoor sight with a soft fade to black.
    forestVision?: boolean
    // Leaves blowing across the world, in daylight.
    windLeaves?: WindLeavesConfig
    // A drifting shader haze, after dark.
    nightFog?: NightFogConfig
    // A butterfly or two wandering about and settling on the grass, in daylight.
    butterflies?: ButterfliesConfig
    // Fireflies hovering over their own small patches of air, after dark.
    fireflies?: FirefliesConfig
    // Sparks off the lava in an ember mine, and bubbles bursting on it.
    lavaEmbers?: LavaEmbersConfig
}

export interface MessageEnvelope {
    contentProtocol?: number
    contentRevision?: string
    recv_ts: number
    payloadType: string
    payload: Message | WorldState | any
}

export interface Movement {
    moving: boolean
    direction: Direction
    angle: number
}

export type FixtureId = string

export interface IdAware {
    entityId: string
}

export interface TypeAware {
    entityType: EntityType
}

export interface UpdateTsAware {
    lastUpdateTs: number | null
}

export type PlayerEventType =
    | "DIALOG_READ"
    | "DIALOG_CLOSE"
    | "UPGRADE_STAT"
    | "CONVERSATION_RESPONSE"
    | "REQUEST_INVENTORY"
    | "REQUEST_CRAFTING"
    | "REQUEST_TRADING"
    | "REQUEST_QUESTS"
    | "SQUAD_COMMAND"
    | "ACTIVATE_TILE_INTERACTION"
    | "CAMPFIRE_MENU_HINT_SHOWN"
    | "RECRUIT_MENU_HINT_SHOWN"
    | "ENTITY_CONTEXT_MENU_OPENED"
    | "REQUEST_ENTITY_INTERACTIONS"
    | "ACTIVATE_ENTITY_INTERACTION"
    // A button on the ember end-of-run summary panel (currently only "retry"). Reaches the ember
    // controller script as a room-wide MARKER_EVENT carrying { command }.
    | "EMBER_RUN_COMMAND"
    | "EMBER_INVASION_COMMAND"
    // The player's answer to the first-run ember tutorial invitation: { command: "accept" | "skip",
    // reason: "click" | "timeout" }. Reaches ember-tutorial.js as a room-wide MARKER_EVENT.
    | "EMBER_TUTORIAL_COMMAND"
    | "EMBER_BUILDING_TUTORIAL_SHOWN"
    // The player pressed "Next" on a HELD speech bubble, so whoever said it may go on:
    // { speakerEntityId, holdId }. Reaches the speaking script as a room-wide MARKER_EVENT. A
    // script that paces a conversation off these acks (ember-tutorial.js) speaks at the player's
    // reading speed instead of its own clock. Deliberately NOT keyed on targetEntityId: the
    // director that does the pacing is a different entity from the one doing the talking.
    | "SPEECH_DISMISSED"
    // A name typed into the name prompt: { targetEntityId, name }. Reaches the named entity's own
    // script as a MARKER_EVENT scoped to it, which is what keeps this from being a way to rename
    // anything at all -- only an entity whose script handles this event can be named, and it
    // decides whether the asking player may. The name is cleaned server-side (sanitizeEntityName).
    | "RENAME_ENTITY"
    // The name prompt was accepted or dismissed: { targetEntityId }.
    | "NAME_PROMPT_DISMISSED"

export interface PlayerEvent {
    playerEventType: PlayerEventType
    context: {}
}

// One row of the ember end-of-run kill breakdown. `key` is the creature's raw identity as the
// server knows it (a wave enemy's `flags.enemyType`, a wilderness enemy's race, "rabbit"); the
// client owns the label/emoji it renders for that key so new creature types still show up.
export interface EmberRunKillTally {
    key: string
    count: number
}

// The one-time "take the tutorial?" invitation put up over a first-time ember camp. Small and
// stage-anchored so it never covers the camp; it resolves itself to `accept` when the countdown
// runs out, so a player who ignores it simply gets the tutorial. Produced by ember-tutorial.js.
export interface EmberTutorialPrompt {
    // Chapter completion reuses this nonmodal board with an explicit, untimed action.
    acceptCommand?: "continue_story" | "begin_chapter"
    hideSkip?: boolean
    announcementChapter?: number
    nextChapterChip?: number
    // Headline + one supporting line. The server owns the copy so it can be retuned without a
    // client release.
    title: string
    body: string
    // Labels on the "take it" and "skip it" buttons.
    acceptLabel: string
    skipLabel: string
    // How long the invitation stays up before it fades and the tutorial begins anyway.
    timeoutMs: number
}

// The stats the ember controller keeps for one run of the camp, shipped to the client when the
// player falls so it can render the summary panel. Produced by ember-controller.js.
export interface EmberRunSummary {
    // Wall-clock length of the run: camp activation -> the player's death.
    survivedMs: number
    // Waves fully cleared, and the highest wave that actually landed.
    wavesCleared: number
    wavesReached: number
    // Creatures killed, by kind, plus the totals (killsByPlayer = the player's own kills).
    kills: EmberRunKillTally[]
    killsTotal: number
    killsByPlayer: number
    // Camp work: trees felled (by anyone), recruits taken into service, wood fed to the fire.
    treesFelled: number
    recruitsHired: number
    fireRekindled: number
    // Lives. A camp grants `livesTotal` of them and every death spends one, so on the panel
    // `livesSpent` is always at least 1. A life is what buys "try again" (get up in this camp and
    // carry the run on), so once they are all spent the camp will not take the player back and
    // only "restart" -- a fresh camp, always offered -- is left. See ember-controller.js.
    livesTotal: number
    livesSpent: number
}

export interface LightMeta {
    radius: number
    intensity: number
}

export interface Light extends LightMeta, IdAware {
    location: Coordinate
    /**
     * Drawn as this many concentric rings instead of one flat disc, each clipped to the same rock, so the
     * light is brightest at its heart and fades to its edge (the organic cave's torch). Default 1.
     */
    falloff?: number
    /**
     * Counted only while its source is inside the viewer's sight polygon. A light is shaped against the
     * rock but not against what the viewer can see, so without this a crystal sealed in a pocket would
     * glow through the rock round it.
     */
    sightGated?: boolean
}

export interface Visibility {
    mask?: VectorLine[]
    /**
     * Occluders authored by hand on the room's `visibility` object layer, kept apart from the ones
     * derived from the tile grid. `mask` is rebuilt from the grid whenever a tile's blocking
     * changes (RoomFactory.updateVisibility), which would otherwise drop these for good the first
     * time anyone placed or broke a wall in such a room. Server-side only; never sent to a client,
     * which receives the finished `mask`.
     */
    authoredMask?: VectorLine[]
    range?: number
    suppressMinimap?: boolean
    // Per-room HUD suppression (rides to the client in WorldState.visibility, like suppressMinimap).
    // When set, the client hides the day/night indicator / top-right control-bar icons / the
    // "Level up stats!" button in this room.
    suppressDayCycle?: boolean
    suppressControlBar?: boolean
    // Squad campaign configuration: hide its icons and disable the J/K/L team shortcuts.
    suppressTeamCommands?: boolean
    suppressLevelUp?: boolean
    // When set, the client hides the player's mana meter / food (hunger) meter in this room.
    suppressMana?: boolean
    suppressFood?: boolean
    // When set, the client hides the shard/resource counter / the player name+level label.
    suppressShards?: boolean
    suppressPlayerName?: boolean
    // When set, the client suppresses tutorial popups (hotbar / control-bar / map / companion) in
    // this room. Ember rooms set this so the camping view stays free of intro tutorials.
    suppressTutorial?: boolean
    // When set, the client hides the standing "/register to save progress" nudge a guest otherwise
    // carries in the corner of every screen. Ember rooms set this: a camp is where a guest is
    // dropped to TRY the game, and a run there is temporary by design -- there is no progress to
    // save yet, so the line is noise over the one view meant to sell them on staying.
    suppressGuestPrompt?: boolean
    // Opt-in (rides to the client in WorldState.visibility, like the suppress* flags): when set, the
    // per-entity/Control-key command wheel is available in this room. Only ember rooms enable it.
    enableCommandWheel?: boolean
    // Opt-in: running (BOOST_ACTION) costs no mana and is not gated by hunger in this room.
    freeBoost?: boolean
    // Ember: running spends stamina while moving, ignores hunger, and uses the same run
    // state for Shift and the mobile stick (rather than scaling the mobile velocity directly).
    staminaRunning?: boolean
    // Ember movement stays under keyboard/stick control, including while attacking.
    suppressClickToMove?: boolean
    // Carrying supplies does not change movement speed in Ember.
    suppressWeightSlowdown?: boolean
    suppressMovementInertia?: boolean
    darkness?: number
    lastUpdateTs?: number
    autoVisibility?: boolean
    fullOcclusion?: boolean
}

export class IndexedMap<T extends IdAware> {
    private _map: Record<string, T> = {}
    private list: string[] = []

    clear = () => {
        this._map = {}
        this.list = []
    }

    get = (id: string) => {
        return this._map[id]
    }

    has = (id: string) => {
        return !!this.get(id)
    }

    includes = (id: string) => {
        return !!this.get(id)
    }

    getAtIdx = (idx: number) => {
        if (idx > this.list.length - 1) {
            return undefined
        }
        return this._map[this.list[idx]]
    }

    pop = (): T => {
        const obj = this.getAtIdx(0)
        if (obj) {
            this.remove(obj.entityId)
        }
        return obj
    }

    keys = () => Object.keys(this._map)

    contains = (id: string): boolean => !!this._map[id]

    size = () => this.list.length

    empty = () => this.size() < 1

    add = (item: T) => {
        return this.set(item.entityId, item)
    }

    set(id: string, item: T, unshift: boolean = false) {
        const isNew = !this._map[id]
        this._map[id] = item

        if (isNew) {
            if (unshift) {
                this.list.unshift(id)
            } else {
                this.list.push(id)
            }
        }
    }

    remove(id: string): T {
        const toRemove = this._map[id]
        if (toRemove) {
            delete this._map[id]
            // splice in place rather than this.list.filter(...), which allocated a new
            // array on every removal. EntityManager.remove calls this once per entity
            // TYPE (8x) per entity removed, and all but one of those calls is a miss --
            // indexOf returns -1 and we now allocate nothing at all for those.
            const idx = this.list.indexOf(id)
            if (idx > -1) {
                this.list.splice(idx, 1)
            }
        }
        return toRemove
    }

    iterate = (visitor: Visitor<T>, predicate?: Predicate<T>) => {
        this.list.forEach(n => {
            if (predicate) {
                if (predicate(this._map[n])) {
                    visitor(this._map[n])
                }
            } else {
                visitor(this._map[n])
            }
        })
    }

    forEach = this.iterate

    map = <V>(op: (item: T, index?: number) => V) => {
        return this.list.map(k => this._map[k]).map(op)
    }

    push = (item: T) => {
        this.set(item.entityId, item)
    }

    filter = (predicate: Predicate<T>) => {
        return this.list.map(n => this._map[n]).filter(predicate)
    }

    find = (predicate: Predicate<T>): T | undefined => {
        const key = this.list.find(n => {
            const item = this._map[n]
            if (predicate(item)) {
                return true
            }
        })
        return key ? this._map[key] : undefined
    }

    collect = (predicate?: Predicate<T>): T[] => {
        if (!predicate) {
            return [...this.list.map(n => this._map[n])]
        }

        const collection: T[] = []
        this.iterate(item => {
            collection.push(item)
        }, predicate)
        return collection
    }
}

export interface IndexedMapItem<T> extends IdAware {
    data: T
}

export class LoopingIndexedMap<T extends IdAware> extends IndexedMap<T> {
    private ctr: number = -1

    next = (): T => {
        this.ctr++
        if (this.ctr > this.size() - 1) {
            this.ctr = 0
        }

        return this.getAtIdx(this.ctr)
    }

    // -1, not 0, because next() PRE-increments: the counter starts life at -1 so the first next()
    // returns index 0, and a reset has to put it back in that same state. Setting it to 0 made the
    // next() after a reset return index 1, so element 0 was skipped every time round. Its one caller
    // is Metabrain.advance, where that meant the FIRST always-run trait of any npc whose pipeline
    // contains a "reset"-returning behaviour never ran at all -- see
    // docs/ember-wave-enemy-stall-analysis.md.
    reset = () => {
        this.ctr = -1
    }
}

export class DoubleKeyedMap<KEY1, KEY2, VAL> {
    key1Map: Map<KEY1, VAL> = new Map()
    key2Map: Map<KEY2, VAL> = new Map()

    set = (val: VAL, key1?: KEY1, key2?: KEY2) => {
        if (key1) {
            this.key1Map.set(key1, val)
        }
        if (key2) {
            this.key2Map.set(key2, val)
        }
    }

    remove = (key: KEY1 | KEY2) => {
        this.key1Map.delete(key as any)
        this.key2Map.delete(key as any)
    }

    get = (key: KEY1 | KEY2): VAL => {
        const v1 = this.key1Map.get(key as any)
        const v2 = this.key2Map.get(key as any)
        return v1 || v2
    }

    getExact = (key: KEY1, key2: KEY1): VAL => {
        const v1 = this.key1Map.get(key as any)
        const v2 = this.key2Map.get(key2 as any)
        return v1 && v2 ? v1 : undefined
    }

    has = (key: KEY1 | KEY2): boolean => {
        return !!this.get(key)
    }
}

export class LightsContainer extends IndexedMap<Light> {
    lastUpdatedTs: number

    set(id: string, item: Light, unshift: boolean = false) {
        super.set(id, item, unshift)
        this.lastUpdatedTs = getCurrentTime()
    }

    remove(id: string): Light {
        const removed = super.remove(id)
        // Only advance the "lights changed" timestamp when a light was actually
        // removed. Entity teardown calls lights.remove() for EVERY entity, including
        // non-light collectables (dropped wood/seeds). Bumping unconditionally
        // invalidated the shadow/visibility cache (the ts is part of its hash), forcing
        // a synchronous sight-polygon recompute + generateTexture on the next frame --
        // a visible hitch each time you walk over a dropped item.
        if (removed) {
            this.lastUpdatedTs = getCurrentTime()
        }
        return removed
    }
}

export class Bounds {
    x1: number
    y1: number
    x2: number
    y2: number
    width: number
    height: number

    centerCoordinate: Coordinate = blankCoordinate()

    constructor(x1: number, y1: number, x2: number, y2: number) {
        this.x1 = x1
        this.y1 = y1
        this.x2 = x2
        this.y2 = y2
        this.width = this.x2 - this.x1
        this.height = this.y2 - this.y1

        this.centerCoordinate.x = Math.floor(this.x1 + this.width / 2)
        this.centerCoordinate.y = Math.floor(this.y1 + this.height / 2)
    }

    static from(x: number, y: number, width: number, height: number) {
        return new Bounds(x, y, x + width, y + height)
    }

    collision(other: Bounds): boolean {
        if (!other) {
            return false
        }
        // Optimized AABB collision detection - early exit on non-collision
        // Check if bounds are separated on any axis (using > instead of >= to maintain touching behavior)
        return !(other.x1 > this.x2 || other.x2 < this.x1 || other.y1 > this.y2 || other.y2 < this.y1)
    }

    encloses(other: Bounds): boolean {
        return this.x1 <= other.x1 && this.x2 >= other.x2 && this.y1 <= other.y1 && this.y2 >= other.y2
    }

    hasNegative(): boolean {
        return this.x1 < 0 || this.x2 < 0 || this.y1 < 0 || this.y2 < 0
    }

    includes(other: Coordinate): boolean {
        return !(other.x > this.x2 || other.x < this.x1 || other.y > this.y2 || other.y < this.y1)
    }

    recompute() {
        this.width = this.x2 - this.x1
        this.height = this.y2 - this.y1

        this.centerCoordinate.x = Math.floor(this.x1 + this.width / 2)
        this.centerCoordinate.y = Math.floor(this.y1 + this.height / 2)
    }

    static createCenteredBounds = (x: number, y: number, width: number, height: number) => {
        return new Bounds(x - width / 2, y - height / 2, x + width / 2, y + height / 2)
    }

    update = (x: number, y: number) => {
        this.x1 = x - this.width / 2
        this.y1 = y - this.height / 2
        this.x2 = x + this.width / 2
        this.y2 = x + this.height / 2

        this.centerCoordinate.x = Math.floor(this.x1 + this.width / 2)
        this.centerCoordinate.y = Math.floor(this.y1 + this.height / 2)

        return this
    }

    center = (): Coordinate => {
        return this.centerCoordinate
    }

    clone = () => {
        return new Bounds(this.x1, this.y1, this.x2, this.y2)
    }

    toString = () => {
        return `${this.x1},${this.y1},${this.x2},${this.y2}`
    }

    expand = (amount: number) => {
        this.x1 -= amount
        this.y1 -= amount
        this.x2 += amount
        this.y2 += amount
        this.recompute()

        return this
    }

    shrink = (amount: number) => {
        this.x1 += amount
        this.y1 += amount
        this.x2 -= amount
        this.y2 -= amount
        this.recompute()

        return this
    }

    translate = (x: number, y: number) => {
        this.x1 += x
        this.x2 += x
        this.y1 += y
        this.y2 += y
        this.recompute()
    }

    isCorner = (x: number, y: number) => {
        if (this.x1 === x && this.y1 === y) {
            return true
        }
        if (this.x2 - 1 === x && this.y1 === y) {
            return true
        }
        if (this.x1 === x && this.y2 - 1 === y) {
            return true
        }
        if (this.x2 - 1 === x && this.y2 - 1 === y) {
            return true
        }

        return false
    }

    visitEdges = (visitor: (x: number, y: number) => void, edge?: Direction) => {
        for (let x = this.topLeft().x; x < this.topLeft().x + this.width; x++) {
            if (!edge || edge === "Up") {
                visitor(x, this.topLeft().y)
            }
            if (!edge || edge === "Down") {
                visitor(x, this.topLeft().y + this.height - 1)
            }
        }

        for (let y = this.topLeft().y; y < this.topLeft().y + this.height; y++) {
            if (!edge || edge === "Left") {
                visitor(this.topLeft().x + this.width - 1, y)
            }
            if (!edge || edge === "Right") {
                visitor(this.topLeft().x, y)
            }
        }
    }

    randomEdge = (edge: Direction): Coordinate | null => {
        if (edge === "Up") {
            return {
                x: randomIntBetween(this.topLeft().x, this.topLeft().x + this.width),
                y: this.topLeft().y,
            }
        }
        if (edge === "Down") {
            return {
                x: randomIntBetween(this.topLeft().x, this.topLeft().x + this.width),
                y: this.topLeft().y + this.height - 1,
            }
        }
        if (edge === "Left") {
            return {
                x: this.topLeft().x,
                y: randomIntBetween(this.topLeft().y, this.topLeft().y + this.height),
            }
        }
        if (edge === "Right") {
            return {
                x: this.topLeft().x + this.width,
                y: randomIntBetween(this.topLeft().y, this.topLeft().y + this.height),
            }
        }
        return null
    }

    getAllCoordinates = (): Coordinate[] => {
        const all = []
        for (let x = this.topLeft().x; x < this.topLeft().x + this.width; x++) {
            for (let y = this.topLeft().y; y < this.topLeft().y + this.height; y++) {
                all.push({ x, y })
            }
        }
        return all
    }

    edgesAreClose = (other: Bounds, threshold: number): boolean => {
        let failed = false
        this.visitEdges((x: number, y: number) => {
            if (failed) {
                return
            }
            other.visitEdges((oX: number, oY: number) => {
                if (failed) {
                    return
                }
                failed = getDistance(x, y, oX, oY) < threshold
            })
        })

        return failed
    }

    widen = (otherBounds: Bounds[]) => {
        otherBounds.forEach((other, i) => {
            if (i === 0) {
                this.x1 = other.x1
                this.y1 = other.y1
                this.x2 = other.x2
                this.y2 = other.y2
            } else {
                if (other.x1 < this.x1) {
                    this.x1 = other.x1
                }
                if (other.x2 > this.x2) {
                    this.x2 = other.x2
                }
                if (other.y1 < this.y1) {
                    this.y1 = other.y1
                }
                if (other.y2 > this.y2) {
                    this.y2 = other.y2
                }
            }
        })

        this.recompute()
    }

    topLeft = (): Coordinate => ({
        x: this.x1,
        y: this.y1,
    })

    topRight = (): Coordinate => ({
        x: this.x2,
        y: this.y1,
    })

    bottomLeft = (): Coordinate => ({
        x: this.x1,
        y: this.y2,
    })

    bottomRight = (): Coordinate => ({
        x: this.x2,
        y: this.y2,
    })

    widenViaCoordinate = (coordinate: Coordinate) => {
        if (this.x1 === -1 || this.y1 === -1 || this.x2 === -1 || this.y2 === -1) {
            this.x1 = coordinate.x
            this.y1 = coordinate.y
            this.x2 = coordinate.x
            this.y2 = coordinate.y
        } else {
            if (this.includes(coordinate)) {
                return
            }

            if (coordinate.x < this.x1) {
                this.x1 = coordinate.x
            }

            if (coordinate.y < this.y1) {
                this.y1 = coordinate.y
            }

            if (coordinate.x > this.x2) {
                this.x2 = coordinate.x
            }

            if (coordinate.y > this.y2) {
                this.y2 = coordinate.y
            }
        }

        this.recompute()
    }
}

export interface Appearance {
    race: Race
    bodyColor: Color
    hair?: Hair
    hairColor?: Color
    nose?: Nose
    noseColor?: Color
    eyes?: Eyes
    eyesColor?: Color
    sex: Sex
    inventoryAppearanceHash: string
    // Public held-object art, independent of the inventory delta. Null explicitly clears it.
    heldObjectSpriteId?: string | null
    // A named Ember humanoid (game-common/ember_sprite/humanoids.ts). When set, the body is drawn by
    // EmberCompositeSprite from generated frames instead of the LPC sheets; race and sex still
    // decide gameplay.
    emberSprite?: string
}

export type EntityCollisionMap = Record<EntityId, EntityId[]>
export type CollisionResult = {
    adjust: Coordinate
    collided: boolean
    collision?: Coordinate
    intersection?: MapCoordinateReport
}
export type CollisionTester = (bounds: Bounds) => boolean
export type CollisionMover = (
    bounds: Bounds,
    translate: Coordinate,
    collisionResult: CollisionResult,
    allowAdjust?: boolean,
) => void
export interface ApplyInputResult {
    movedX: boolean
    movedY: boolean
    collision?: Coordinate
    intersection?: MapCoordinateReport
}
export const DefaultManaRecoveryDelay = 600
export const DefaultBoostCostLimit = 6
export const EntityManaRecoveryDelayLimit = 200
export const EntityBoostCostLimit = 2

export class EntityMeta {
    name: string
    entityType: EntityType
    location: Location
    npcType?: NpcType
    strikeable?: boolean
    scanner?: boolean
    shooter?: boolean
    seeker?: SeekerType
    scanPlayersOnly?: boolean
    patrolLocations?: Coordinate[]
    patrolPause?: number
    randomizedPauses?: boolean
    speed?: number
    damage?: number
    damagingOnContact?: boolean
    targetLocationThresholdDistance?: number
    flags?: {}
    context?: {}
    spriteId?: string
    clickable?: boolean
    faction?: Faction
    appearance?: Appearance
    leaderFlags?: {}
    lightMeta?: LightMeta
}

export interface Weapon {
    profileId?: string
    weaponType: WeaponType
}

export type AbilityType = "heal" | "mana" | "speed" | "bomb"

export interface DeviceInfo {
    type: "web" | "mobileWeb"
}

export type Chronotype = "diurnal" | "nocturnal" | "both"

export type AttackPattern = "default" | "swoop" | "v2"

export type AggroResponse = "default" | "pause"

export interface PsychProfile {
    hostility: number
    chronotype?: Chronotype
    attackPattern?: AttackPattern
    aggroResponse?: AggroResponse
    closeOnStun?: boolean
}

export interface EntityContext {
    outpostMeta: OutpostMeta
    collectionMeta: any
    lightingMeta: {
        max: LightMeta
        lastUpdateTs: number
    }
    locationIcons: Record<string, LocationIcon>
    bullet: {
        batchId: string
    }
    denizenContext: any
    convoMeta: any
    questContext: any
    questActiveMarkerId: string
    questsContainer: QuestsContainer
    introState: any
}

export type PartialEntityContext = Partial<EntityContext>

export type EntityFlags = Record<string, boolean | number | any>

export class Entity implements IdAware, TypeAware {
    // Client-side interest lifecycle. Server entities remain authoritative; this is only the
    // render/update contract used to prevent stale cached positions from becoming visible.
    interestState: "active" | "dormant" = "active"
    maxSpeed: number = Mechanics.entity.speed.minimum
    speed: number = Mechanics.entity.speed.minimum
    speed2: Coordinate = { x: 0, y: 0 }
    crossFloorProjectile?: boolean
    projectileTarget?: Coordinate
    landedArrow?: boolean

    movement: Movement
    simpleDir?: string
    entityId: EntityId
    accountId?: string
    entityType: EntityType
    ownerEntityId?: EntityId

    location: Coordinate
    origin?: Coordinate

    maxDistance?: number

    position_buffer = []
    input_sequence_number: number = 0
    pending_inputs: Message[] = []

    entityState: EntityState = "live"
    entityStateTs: number

    lastMoveTs: number = 0
    hitByEntityIdTs: Record<EntityId, number> = {}

    bounds: Bounds
    fixedBounds?: {
        x1: number
        y1: number
        x2: number
        y2: number
    }

    name?: string

    maxHp: number = 3
    hp: number = 3
    mana: number = 20
    maxMana: number = 20
    hunger: number = 20
    maxHunger: number = 20
    manaRecoveryDelay: number = 600
    boostPct: number = 0.0
    boostCost: number = DefaultBoostCostLimit
    weight: number = 0

    damage: number = 1
    damagingOnContact: boolean

    lastStruckByEntityId?: EntityId
    lastStruckTs?: number
    lastStruckEntityId?: EntityId
    lastStruckEntityTs?: number

    lastAlarmedByEntityId?: EntityId
    lastAlarmedTs?: number
    lastKilledTs?: number

    npcType: NpcType
    spriteId?: string
    noGlint?: boolean
    spriteScale?: number
    strikeable?: boolean
    immoveable?: boolean

    color?: number
    visible: boolean = true
    // Runtime only: the chapter title screen hides the player until its fade-out finishes.
    emberChapterAnnouncement?: number
    creationTs?: number
    preSpawnEffect?: PreSpawnEffect | null

    flags: EntityFlags = {}
    dimensions: Dimensions
    debug?: boolean

    roomId: EntityId
    lastUpdateTs: number | null = null
    lastStatsUpdateTs: number
    lastRoomUpdateTs: number = 0

    canShootBullets?: boolean
    canLayBomb?: boolean

    boosted?: boolean
    // Whether the RUN KEY IS DOWN, as against `boosted`, which is whether the entity is actually
    // running right now. The two used to be one field, and that is why a held Shift used to stop
    // working: the client sends one BOOST_ACTION_ON per keydown (modifier keys do not auto-repeat),
    // so when the boost latch could no longer afford the spend and cleared `boosted`, nothing was
    // left to turn it back on and the player walked for the rest of the hold. Separating the INTENT
    // from the STATE lets the latch drop the run and pick it up again on its own.
    boostHeld?: boolean
    // Set once this entity has actually RUN: the key held and the body moving for
    // `Mechanics.entity.speed.runLearnedMs`, accrued in campaigns/default. NOT "has pressed the
    // key", which it used to be and which is a bad proxy -- Shift also opens the building shape
    // palette, so a player laying a wall stamped it without taking a step, and that hid the camp's
    // run hint from exactly the beginners it is for (see campaigns/ember/run_hint.ts). Completion is
    // saved in flags.runTutorialCompleted and restored here when the character is loaded.
    hasBoosted?: boolean
    portable?: boolean
    joinedTeam?: boolean

    guest?: boolean
    score?: number
    scoreTilNextLevel?: number
    level?: number

    paralyzeUntilTs?: number
    npcSafeUntilTs?: number

    // DOWNED: a player who has fallen and is waiting to be put back on their feet. They keep their
    // place in the world (and, in an ember camp, sit in front of the end-of-run scorecard) but
    // they do not get to walk around while they are there -- see ServerGameLogic.advancePlayer.
    //
    // It is a DEADLINE, not a flag, and that is the whole point: whoever owes this player a
    // respawn may never deliver -- a room destroyed under them, a controller script that threw, a
    // client that never answered its panel -- and a plain boolean would leave them frozen forever.
    // The deadline is the outer bound on that; ServerGameLogic.cleanup stands up anyone who
    // reaches it. Deliberately NOT persisted to the account either (see AccountManager.save), so
    // logging back in is an escape hatch of its own.
    downedUntilTs?: number
    /**
     * A performance this body is playing (ember_sprite/animations.ts: a wave, a bow): its name, when
     * it began and whether it repeats. The server's truth; clients see it as `animationPose`
     * (server/src/performances.ts performancePose). Not persisted (see AccountManager.save).
     */
    performance?: { name: string; startedAt: number; loop: boolean }
    floorFallUntilTs?: number
    stairTravelUntilTs?: number
    stairTravelDestination?: Location
    stairTravelArrival?: StairTravelView
    floorFallRoomId?: string
    /** Consume on room entry after a seamless building-level transfer. */
    suppressRoomEntryFadeRoomId?: string
    /**
     * CLIENT-SIDE MIRROR of Message.invading -- whether this entity is a player on an invasion,
     * wearing one of a camp's attackers. The server keeps the truth on flags (INVASION_FLAG), which
     * never reach a client, so this is the verdict as the wire reported it.
     *
     * What reads it is the ground RING that marks a player apart from an NPC: a possessor is still a
     * player entity, so without this the defender gets a player ring under one of the sixteen things
     * attacking their camp, which is the single clearest tell there could be.
     */
    invading?: boolean
    /**
     * CLIENT-SIDE MIRROR of Message.rescuePose. An injured ember survivor lies face-down where it
     * waits and limps while it follows; LpcComposite.doMovement reads this. Flags carry the truth
     * on the server and never reach a client, so the wire verdict is all the renderer has.
     */
    rescuePose?: "downed" | "following"
    /**
     * CLIENT-SIDE MIRROR of Message.rescueHelpHint -- the server's verdict that the prompt to hold
     * on an injured survivor and help her up belongs on screen. RescueHint draws off this; nothing
     * else reads it.
     *
     * A verdict rather than a condition the client evaluates, like `rescuePose` beside it: whether
     * the hold would be accepted at all is the rescue's business (the wreck has to be clear of
     * skeletons first), and flags never reach a client.
     */
    rescueHelpHint?: boolean
    chargedWeaponDefinition?: ChargedWeaponDefinition
    weaponCharge?: WeaponChargeView
    visualEffects?: Effect[]
    floorFall?: FloorFallView
    stairTravel?: StairTravelView
    animationPose?: AnimationPoseView
    attackWindup?: AttackWindupView
    areaProjectile?: AreaProjectileView
    bonecallerAttack?: BonecallerAttack
    bonecallerCombatRadius?: number
    bonecallerRitual?: BonecallerRitual
    preSpawnEffectView?: PreSpawnEffectView
    bonecallerEmergenceRemainingMs?: number
    /**
     * CLIENT-SIDE MIRROR of Message.downed -- whether this body has FALLEN and not yet been stood
     * back up. The truth is the deadline above (`downedUntilTs`), or a lord's critical health,
     * which the server evaluates, so
     * like `invading` and `rescuePose` this is the server's per-message verdict and nothing else.
     *
     * What reads it is LpcComposite.doMovement, which lays a humanoid body down in the final frame
     * of the LPC `hurt` row for as long as it is set. Deliberately NOT the deadline: a client
     * driving the pose off its own clock would hold a body prone past the moment the server stood
     * it up, or let it up early, whenever the two clocks disagreed.
     */
    downed?: boolean
    /**
     * CLIENT-SIDE MIRROR of Message.holdRemainingMs / Message.holdDurationMs -- the live countdown
     * of a held interaction somebody is performing on this entity ("hold to help up" on an injured
     * ember survivor). HoldTimer draws the dial off these; nothing else reads them.
     *
     * Like `downed`, the remaining time is the server's own per-message verdict rather than a
     * deadline this client would have to resolve against its own clock -- and here the verdict is
     * the point: the server decides whether a hold is running at all (reach, threats, lease), so a
     * locally timed dial could fill up for a hold that was refused the moment it began.
     */
    holdRemainingMs?: number
    holdDurationMs?: number
    // Public context-menu onboarding state; server-only entity flags are not transmitted.
    rainDamaged?: boolean
    contextMenuSubject?: "campfire" | "recruit"
    campfireMenuLearned?: boolean
    recruitMenuLearned?: boolean


    weapon: Weapon
    inventory?: Inventory

    leaderEntityId?: EntityId
    deviceInfo?: DeviceInfo

    upgradePoints: number
    appearance?: Appearance

    clickable?: boolean
    context?: PartialEntityContext

    serverOnly?: boolean
    expirationTs?: number
    persistable?: boolean

    faction?: Faction
    quantity: number

    carried: boolean

    spawnPoint?: Location

    lightMeta?: LightMeta

    movingBackwards?: boolean

    submerged?: boolean

    /** Client only: its feet are in the water layer's water this frame (water_layer.ts). */
    wading?: boolean

    superuser?: boolean

    afk?: boolean

    active?: boolean

    constructor() {
        this.location = { x: -1, y: -1 }
        this.roomId = "tutorial1"
        this.origin = { x: -1, y: -1 }
        this.movement = {
            moving: false,
            direction: "Up",
            angle: 0,
        }
        this.position_buffer = []
        this.inventory = {
            items: [],
            abilities: {},
            lastUpdateTs: getCurrentTime(),
        }
        this.upgradePoints = 0
        this.level = 1
    }

    static fromJson = (json: any): Entity => {
        if (!json) {
            return
        }
        const entity: Entity = new Entity()
        entity.fromJson(json)
        return entity
    }

    fromEntity = (entity: Entity) => {
        this.fromJson(JSON.parse(JSON.stringify(entity)))
    }

    fromJson = (json: any): Entity => {
        if (!json) {
            return
        }
        Object.keys(json).forEach(key => {
            const value = json[key]
            if (typeof this[key] !== "function") {
                this[key] = value
            }
        })
        const fixedBounds = json.fixedBounds
        this.bounds = undefined
        if (fixedBounds) {
            this.fixedBounds = fixedBounds
            this.bounds = new Bounds(fixedBounds.x1, fixedBounds.y1, fixedBounds.x2, fixedBounds.y2)
        }
    }

    serializeToString = (): string => {
        return `${this.entityId}|${this.entityType}|${this.npcType || ""}|${this.faction || ""}|${
            this.entityState || ""
        }|${this.location.x}|${this.location.y}|${this.visible === true}|${this.strikeable}|${
            this.leaderEntityId || ""
        }|${this.dimensions.w}|${this.dimensions.h}|${this.expirationTs}|${
            this.flags ? JSON.stringify(this.flags) : ""
        }|${this.fixedBounds ? JSON.stringify(this.fixedBounds) : ""}|${JSON.stringify(this.movement)}|${
            this.paralyzeUntilTs
        }|${this.npcSafeUntilTs}|${this.afk === true}|${this.carried === true}|${this.speed}|${this.maxSpeed}|${
            this.lastStruckByEntityId || ""
        }|${this.lastStruckTs}|${this.lastStruckEntityId || ""}|${this.lastStruckEntityTs}`
    }

    static fromString = (roomId: string, text: string): Entity => {
        if (!text) {
            return
        }
        const [
            entityId,
            entityType,
            npcType,
            faction,
            entityState,
            x,
            y,
            visible,
            strikeable,
            leaderEntityId,
            dimensionsW,
            dimensionsH,
            expirationTs,
            flags,
            fixedBounds,
            movement,
            paralyzeUntilTs,
            npcSafeUntilTs,
            afk,
            carried,
            speed,
            maxSpeed,
            lastStruckByEntityId,
            lastStruckTs,
            lastStruckEntityId,
            lastStruckEntityTs,
        ] = text.split("|")
        const entity: Entity = new Entity()

        entity.entityId = entityId
        entity.entityType = entityType as EntityType
        entity.npcType =
            npcType !== "null" && npcType !== "undefined" && npcType !== "" ? (npcType as NpcType) : undefined
        entity.faction =
            faction !== "null" && faction !== "undefined" && faction !== "" ? (faction as Faction) : undefined
        entity.entityState =
            entityState !== "null" && entityState !== "undefined" && entityState !== ""
                ? (entityState as EntityState)
                : undefined
        entity.leaderEntityId =
            leaderEntityId !== "null" && leaderEntityId !== "undefined" && leaderEntityId !== ""
                ? (leaderEntityId as EntityId)
                : undefined
        entity.expirationTs =
            expirationTs !== "null" && expirationTs !== "undefined" ? parseInt(expirationTs) : undefined
        entity.flags = flags !== "" ? JSON.parse(flags) : undefined
        entity.paralyzeUntilTs =
            paralyzeUntilTs !== "null" && paralyzeUntilTs !== "undefined" ? Number.parseInt(paralyzeUntilTs) : undefined
        entity.npcSafeUntilTs =
            npcSafeUntilTs !== "null" && npcSafeUntilTs !== "undefined" ? Number.parseInt(npcSafeUntilTs) : undefined
        entity.carried = carried !== "null" && carried !== "undefined" ? carried === "true" : undefined
        entity.afk = afk === "true"

        const location: Coordinate = {
            x: parseFloat(x),
            y: parseFloat(y),
        }
        entity.location = location
        entity.roomId = roomId
        entity.visible = visible === "true"
        entity.strikeable = strikeable === "true"

        entity.dimensions = {
            w: parseInt(dimensionsW),
            h: parseInt(dimensionsH),
        }

        if (fixedBounds) {
            entity.fixedBounds = JSON.parse(fixedBounds)
            const { x1, y1, x2, y2 } = entity.fixedBounds
            entity.bounds = new Bounds(x1, y1, x2, y2)
        }

        if (movement) {
            entity.movement = JSON.parse(movement)
        }
        entity.speed = Number(speed)
        entity.maxSpeed = Number(maxSpeed)

        entity.lastStruckByEntityId = lastStruckByEntityId
        entity.lastStruckTs =
            lastStruckTs !== "null" && lastStruckTs !== "undefined" ? parseInt(lastStruckTs) : undefined
        entity.lastStruckEntityId = lastStruckEntityId
        entity.lastStruckEntityTs =
            lastStruckEntityTs !== "null" && lastStruckEntityTs !== "undefined"
                ? parseInt(lastStruckEntityTs)
                : undefined

        return entity
    }

    getGrossOrientation = (): "LeftRight" | "UpDown" => {
        const direction = radiansToCardinalDirection(this.movement.angle)
        if (["Left", "Right"].includes(direction)) {
            return "LeftRight"
        } else {
            return "UpDown"
        }
    }

    getCollisionBounds = (_x?: number, _y?: number): Bounds => {
        if (this.fixedBounds) {
            return this.bounds
        }
        const x = _x || this.location.x
        const y = _y || this.location.y

        if (!this.bounds) {
            this.bounds = new Bounds(0, 0, 0, 0)
        }

        if (Mechanics.entity.body.calculateCollisionBounds(this, this.bounds)) {
            // computed!
        } else {
            this.bounds.x1 = x - this.dimensions.w / 2
            this.bounds.y1 = y - this.dimensions.h / 2
            this.bounds.x2 = x + this.dimensions.w / 2
            this.bounds.y2 = y + this.dimensions.h / 2
        }

        this.bounds.recompute()

        return this.bounds
    }

    getBounds = (_x?: number, _y?: number): Bounds => {
        if (this.fixedBounds) {
            return this.bounds
        }
        const x = _x || this.location.x
        const y = _y || this.location.y

        if (!this.bounds) {
            this.bounds = new Bounds(0, 0, 0, 0)
        }

        this.bounds.x1 = x - (this.dimensions?.w || TileSize) / 2
        this.bounds.y1 = y - (this.dimensions?.h || TileSize) / 2
        this.bounds.x2 = x + (this.dimensions?.w || TileSize) / 2
        this.bounds.y2 = y + (this.dimensions?.h || TileSize) / 2
        this.bounds.recompute()

        return this.bounds
    }

    getNavigationBounds = (_x?: number, _y?: number): Bounds => {
        if (this.fixedBounds) {
            return this.bounds
        }
        const x = _x || this.location.x
        const y = _y || this.location.y

        if (!this.bounds) {
            this.bounds = new Bounds(0, 0, 0, 0)
        }

        // Always use standard 25x25 for navigation, same as players
        this.bounds.x1 = x - 25 / 2
        this.bounds.y1 = y - 25 / 2
        this.bounds.x2 = x + 25 / 2
        this.bounds.y2 = y + 25 / 2
        this.bounds.recompute()

        return this.bounds
    }

    updateDimensions = (w: number, h: number) => {
        this.dimensions = {
            w,
            h,
        }

        return this.getBounds()
    }

    travelledDistance = () => {
        return getDistance(this.origin?.x, this.origin?.y, this.location.x, this.location.y)
    }

    updateMana = (amt: number) => {
        this.mana = Math.max(0, Math.min(this.maxMana, this.mana + amt))
    }

    updateHp = (amt: number) => {
        this.hp = Math.max(0, Math.min(this.maxHp, this.hp + amt))
    }

    updateHunger = (amt: number) => {
        this.hunger = Math.max(0, Math.min(this.maxHunger, this.hunger + amt))
    }

    hungerPct = () => {
        return this.hunger / this.maxHunger
    }

    applyInput(
        angle: number | undefined,
        press_timeRaw: number,
        speed2: Coordinate | undefined,
        applyInputResult: ApplyInputResult,
        collisionMover?: CollisionMover,
        allowAdjust?: boolean,
        allowSlide?: boolean,
        useNavigationBounds?: boolean,
    ) {
        if (this.stairTravel?.climb && !this.isDead() && !this.isDowned() && !this.downed &&
            !this.floorFall && this.emberChapterAnnouncement === undefined) {
            if (advanceStairClimb(this, speed2, press_timeRaw)) {
                applyInputResult.movedX = false
                applyInputResult.movedY = false
                return
            }
        }
        if (this.isDownedForMovement() || this.emberChapterAnnouncement !== undefined) {
            this.speed2.x = 0
            this.speed2.y = 0
            applyInputResult.movedX = false
            applyInputResult.movedY = false
            return
        }
        const a = !speed2 ? undefined : getAngle(speed2, { x: 0, y: 0 })
        const diagonal: boolean = a === undefined ? false : this.isPlayer() ? isDiagonal(a) : false
        const press_time = diagonal ? press_timeRaw * 0.7 : press_timeRaw

        let x = 0,
            y = 0
        let intersection = applyInputResult.intersection

        if (angle !== undefined) {
            this.movement.angle = angle
        }

        const initialX = this.location.x
        const initialY = this.location.y
        if (speed2 !== undefined) {
            if (allowSlide) {
                const xItem = CoordinatePool.instance.checkout()
                const yItem = CoordinatePool.instance.checkout()
                const resultX = CollisionResultPool.instance.checkout()
                const resultY = CollisionResultPool.instance.checkout()

                try {
                    x += speed2.x * press_time
                    xItem.data.x = x
                    xItem.data.y = 0
                    const boundsToUseX = useNavigationBounds ? this.getNavigationBounds() : this.getBounds()
                    collisionMover(boundsToUseX, xItem.data, resultX.data, allowAdjust)
                    const { adjust: adjustX, collided: collidedX, collision: c1, intersection: i1 } = resultX.data

                    y += speed2.y * press_time
                    yItem.data.x = 0
                    yItem.data.y = y
                    this.location.x = Math.max(adjustX.x, 0)
                    const boundsToUseY = useNavigationBounds ? this.getNavigationBounds() : this.getBounds()
                    collisionMover(boundsToUseY, yItem.data, resultY.data, allowAdjust)
                    const { adjust: adjustY, collided: collidedY, collision: c2, intersection: i2 } = resultY.data

                    this.location.y = Math.max(adjustY.y, 0)

                    const collision = collidedX ? c1 : c2
                    applyInputResult.collision.x = collision?.x
                    applyInputResult.collision.y = collision?.y

                    applyInputResult.intersection.x = (
                        !emptyCoordinate(i1) ? i1 : !emptyCoordinate(i2) ? i2 : undefined
                    )?.x
                    applyInputResult.intersection.y = (
                        !emptyCoordinate(i1) ? i1 : !emptyCoordinate(i2) ? i2 : undefined
                    )?.y
                    applyInputResult.intersection.intersectionType = (
                        !emptyCoordinate(i1) ? i1 : !emptyCoordinate(i2) ? i2 : undefined
                    )?.intersectionType
                } finally {
                    CoordinatePool.instance.release(xItem)
                    CollisionResultPool.instance.release(resultX)
                    CoordinatePool.instance.release(yItem)
                    CollisionResultPool.instance.release(resultY)
                }
            } else {
                const item = CoordinatePool.instance.checkout()
                const result = CollisionResultPool.instance.checkout()
                try {
                    x += speed2.x * press_time
                    y += speed2.y * press_time

                    item.data.x = x
                    item.data.y = y

                    const boundsToUse = useNavigationBounds ? this.getNavigationBounds() : this.getBounds()
                    collisionMover(boundsToUse, item.data, result.data, allowAdjust)
                    const { adjust, collided, collision, intersection: i1 } = result.data

                    fillCoordinate(i1, intersection)
                    intersection.intersectionType = i1.intersectionType

                    if (collided) {
                        applyInputResult.movedX = false
                        applyInputResult.movedY = false
                        applyInputResult.collision.x = collision.x
                        applyInputResult.collision.y = collision.y
                        fillCoordinate(intersection, applyInputResult.intersection)
                        applyInputResult.intersection.intersectionType = intersection.intersectionType
                        return
                    }

                    this.location.x = Math.max(adjust.x, 0)
                    this.location.y = Math.max(adjust.y, 0)
                } finally {
                    CoordinatePool.instance.release(item)
                    CollisionResultPool.instance.release(result)
                }
            }

            applyInputResult.movedX = this.location.x !== initialX
            applyInputResult.movedY = this.location.y !== initialY
            if (applyInputResult.movedX || applyInputResult.movedY) this.lastMoveTs = getCurrentTime()
        } else {
            // No movement input, so no movement
            applyInputResult.movedX = false
            applyInputResult.movedY = false
        }
    }

    isDead = () => this.entityState === "dead"
    markDead = () => {
        clearStairTravel(this)
        return this.entityState = "dead"
    }
    isPlayer = () => this.entityType === "player"
    isParalyzed = () => (this.paralyzeUntilTs || 0) > getCurrentTime()
    isDowned = () => (this.downedUntilTs || 0) > getCurrentTime()
    // Includes the server's fallen NPC states and their client-side pose mirrors.
    // Keep isDowned's respawn deadline semantics separate from these movement rules.
    isDownedForMovement = () => (!!this.floorFall && !this.floorFall.stairJump) || !!this.stairTravel || this.isDowned() || this.downed === true || this.rescuePose === "downed" ||
        (!!this.flags?.isSurvivor && !this.flags?.rescueFollowing) ||
        (this.flags?.isLord === true && !this.isDead() && this.maxHp > 0 && this.hp < this.maxHp * 0.2)
    /**
     * Has this body FALLEN -- dead, or downed by either road: the server's own deadline
     * (`downedUntilTs`) or the per-message verdict the client mirrors onto `downed`. A client only
     * ever sees the second of those, which is why asking `isDowned()` alone answers no on the very
     * machine that is drawing the death screen.
     *
     * Deliberately NOT isDownedForMovement: that also counts a Lord at critical health and a
     * survivor awaiting rescue, which are movement rules. A Lord on 19% hp is very much still alive
     * as far as a health meter is concerned, and must not be drawn with empty hearts.
     */
    hasFallen = () => this.isDead() || this.isDowned() || this.downed === true

    isNpcSafe = () => (this.npcSafeUntilTs || 0) > getCurrentTime()

    safeName = () => {
        let name = this.name

        if (!name) {
            name =
                this.appearance?.race ||
                (this.flags?.isCampfire ? "campfire" : undefined) ||
                (this.flags?.isFire ? "fire" : undefined) ||
                this.npcType ||
                this.entityType
            name = this.isPlayer() ? `${name} player` : name
        }

        const max = 15
        if (name?.length > max) {
            return name.substring(0, max - 3) + "..."
        }

        return name
    }

    resetHitByEntityIdTs = () => {
        this.hitByEntityIdTs = {}
    }

    canPersist = () => {
        return this.expirationTs === undefined && this.persistable === true
    }

    lightIntensity = () => {
        return this.npcType === "light" ? 0.8 : this.lightMeta ? this.lightMeta.intensity : 0.6
    }

    lightRadius = () => {
        return this.npcType === "light" ? 50 : this.lightMeta ? this.lightMeta.radius : 30
    }
}

export type CoordinateStructure<T> = Array<Array<T>>

export type TileMapBase<T> = CoordinateStructure<T>
export type TileMap = TileMapBase<string>
export type TileLayerMapping = { maptype: "tilemap" | "sprited" | "sky" | "skyTrigger" | "occlusion"; idx: number }
export type TileMapLayerMapping = Record<string, TileLayerMapping>
export type CollisionMap = Array<Array<0 | 1>>

export type Predicate<T> = (item: T) => boolean

export type Visitor<T> = (item: T) => void

export const getDistance = (x1: number, y1: number, x2: number, y2: number) => {
    let y = x2 - x1
    let x = y2 - y1

    return Math.sqrt(x * x + y * y)
}

export const getCoordinateDistance = (a: Coordinate, b: Coordinate) => getDistance(a.x, a.y, b.x, b.y)

export const getEntityDistance = (e1: Entity, e2: Entity): number => {
    return getDistance(e1.location.x, e1.location.y, e2.location.x, e2.location.y)
}

export const randomIntBetween = (min, max, options?: { seed?: string; rngFn?: Function }) => {
    // min and max included
    if (options?.seed) {
        return randomIntBetweenRng(min, max, options.seed)
    }

    const fnToUse = options?.rngFn || Math.random

    return Math.floor(fnToUse() * (max - min + 1) + min)
}

export const randomFloatBetween = (min: number, max: number) => {
    const str = (Math.random() * (max - min) + min).toFixed(3)
    return parseFloat(str)
}

export const pctChance = (
    pct: number,
    options?: {
        randomIntBetween?: Function
        seed?: string
    },
) => {
    // min and max included
    if (options?.randomIntBetween) {
        return options.randomIntBetween(0, 100) >= 100 - pct
    }

    if (options?.seed) {
        return randomIntBetween(0, 100, { seed: options.seed }) >= 100 - pct
    }

    return randomIntBetween(0, 100) >= 100 - pct
}

export const randomBool = () => {
    return pctChance(50)
}

export interface EmberChapterChecklist {
    chapter: number
    title: string
    tasks: { id: string; label: string; complete: boolean }[]
}

export interface ScreenMessage {
    chapterChecklist?: EmberChapterChecklist | null
    stairChoices?: Record<"up" | "down", PlayerEvent>
    text?: string
    dynamicText?: (DynamicTextMeta | string)[]
    target?: string
    roomId?: EntityId
    entityId?: EntityId
    duration?: number
    delay?: number
    displayCallback?: Callback
    readEvent?: string
    forceTarget?: boolean
    progressive?: boolean
    action?: "close" | "openDynamicDialog"
    actionToken?: any
    context?: any
    columnCount?: number
    onCloseEmitEvent?: PlayerEvent
    suppressOnCloseEmitEvent?: boolean
    messageId?: string
}

export type TileType =
    | "blocking"
    | "hidden"
    | "dense"
    | "denseVertical"
    | "entityTile"
    | "deep"
    | "lava"
    | "floor"
    | "occlusion"
    | "collideable"

export interface TileBreakageMeta {
    descriptors: {
        tilesetId: string
        compositeTileId: string
        thresholdLow: number
        thresholdHigh?: number
    }[]
}

export interface TileMeta {
    tileTypes: TileType[]
    /** Ground rectangles relative to the parent cell, including sprite offsets.
     * May extend one cell beyond each edge; canopy pixels are not blockers. */
    blockingRects?: TileBlockingRect[]
    compositeTileId?: string
    compositeTileParent?: boolean
    malleable?: boolean
    spriteId?: string
    entityTileId?: EntityTile
    renderZIdxOffset?: number
    renderYOffset?: number
    renderXOffset?: number
    /** How many times a `denseVertical` plant is stacked in its cell; unset, the renderer picks 1-3. */
    denseCopies?: number
    /** A composite plant drawn without a ground shadow: the client casts one in the sun (foliage_shadow.ts). */
    castsShadow?: boolean
    variant?: string
    breakageMeta?: TileBreakageMeta
    allowedDirections?: Direction[]
    notAllowedDirections?: Direction[]
}

export const createBlankTileMeta = (): TileMeta => {
    return {
        tileTypes: [],
    }
}

// a tile can have many properties
// tileId -> tile properties
export type TileMapMeta = Record<string, TileMeta>

// a tileset can have many tiles
// and metadata about the tileset
export interface Tileset {
    width: number
    height: number
    tilesetId: string
    tiles: TileMapMeta
    firstGid: number
}

// tilesetId -> Tileset
export type TilesetDirectory = Record<
    string,
    {
        meta: TileMapMeta
        dimensions: Dimensions
    }
>

// a map can have many tilesets:
// mapId -> Tilesets
export type AtlasDirectory = Record<string, Tileset[]>

export interface LayerDescriptor {
    entityId: string
    layerClass: TileLayerMapping
    tilemap: TileMap
}

export interface MapSpriteCap {
    x: number
    y: number
    context?: any
}

export interface TileMapDirectory {
    twoStoreyHouse?: import("./room/ember/two_storey_house").TwoStoreyHouse
    mapId: string
    tilemaps: TileMap[]
    blocking: TileMap
    occlusion: TileMap
    entityTilemapIdx?: number
    entityTileLayerId?: string
    sky?: TileMap[]
    skyTrigger?: TileMap
    /** Where the ground is raised (ember banks and knolls): "1" per raised cell. Data only, never drawn as tiles. */
    rise?: TileMap
    /**
     * An organic cave's plan, one OrganicRock code per cell (room/cave/organic_cave.ts). Data only: the client
     * draws the rock from it (rock_mass.ts) instead of a sprite per rock tile, and buildPolyMap sets the
     * occluders back to the drawn faces.
     */
    organicRock?: TileMap
    /** The organic cave's veins, crystals and pools; sent with organicRock. */
    organicCave?: import("./room/cave/organic_cave").OrganicCaveFeatures
    /** Where there is water (the ember pond): "1" shallow, "2" deep, per cell. Data only, drawn by the client's water layer. */
    water?: TileMap
    sprited?: TileMap[]
    spriteFragments?: FragmentSpriteMeta[]
    mapSpriteCaps: MapSpriteCap[]
    deltas: Record<string, Record<string, MapDelta>> // layer -> position coordinate -> map delta
    layerIdToTileMap?: TileMapLayerMapping
}

export type Biome = string

export interface WorldMetaDimensions {
    width: number
    height: number
    quadrantWidth: number
    quadrantHeight: number
    roomWidth: number
    roomHeight: number
    roomsCountX: number
    roomsCountY: number
    sectorWidth: number
    sectorHeight: number
}

export interface WorldMeta {
    id: string
    dimensions: WorldMetaDimensions
    roomBiomesDirectory: Record<string, Biome[]>
    biomesToRooms: Record<Biome, string[]>
    allBiomes: Biome[]
    biomeColorMap: TileMapBase<number>
    locationIconsMap: Record<string, LocationIcon>
    geographicFeatures: Record<string, GeographicFeature[]>
    mapFeatures: Record<string, MapFeature[]>
}

export type MapIntersectionType = "" | "blocking" | "entityTile" | "collideable"

export interface FragmentMapEntry {
    x: number
    y: number
    tilePos: number
    renderZIdxOffset?: number
    roofLayer: "below"
}

export interface FragmentSpriteMeta {
    id: string
    mapEntries: FragmentMapEntry[]
    width: number
    height: number
    location: Coordinate
    fineLocationNudge?: Coordinate
    debugTxt?: string
}

export interface NumberedTileMeta {
    tileMeta: TileMeta
    tileNumber: number
    children?: NumberedTileMeta[]
}

export const tiledCollisionTester = (tilemapMeta: TileMapMetaProvider): CollisionTester => {
    const { isCollisionAt } = tilemapMeta

    const func = (bounds: Bounds): boolean => {
        const x = clamp(Math.floor(bounds.x2 / TileSize), 0, tilemapMeta.width())
        const y1 = clamp(Math.floor(bounds.y1 / TileSize), 0, tilemapMeta.height())
        const y2 = clamp(Math.floor(bounds.y2 / TileSize), 0, tilemapMeta.height())
        for (let y = y1; y <= y2; y++) {
            if (x > tilemapMeta.width() - 1 || y > tilemapMeta.height() - 1) {
                continue
            }
            if (isCollisionAt(x, y)) {
                return true
            }
        }

        const x1 = clamp(Math.floor(bounds.x1 / TileSize), 0, tilemapMeta.width())
        const x2 = clamp(Math.floor(bounds.x2 / TileSize), 0, tilemapMeta.height())
        const y = clamp(Math.floor(bounds.y1 / TileSize), 0, tilemapMeta.height())
        for (let x = x1; x <= x2; x++) {
            if (x > tilemapMeta.width() - 1 || y > tilemapMeta.height() - 1) {
                continue
            }
            if (isCollisionAt(x, y)) {
                return true
            }
        }
    }

    return func
}

export const collisionMover = (tilemapMetaProvider: TileMapMetaProvider, allowAdjust?: boolean,
    useGroundFootprints = true, allowEscapeFromOverlap = false): CollisionMover => {
    const { isCollisionAt, intersectionAt } = tilemapMetaProvider

    const func = (bounds: Bounds, translate: Coordinate, result: CollisionResult, adjust = allowAdjust) => {
        if (useGroundFootprints && moveAgainstTileProfiles(tilemapMetaProvider, bounds, translate, result, !!adjust)) return
        let blocked = false
        const start = { x1: bounds.x1, y1: bounds.y1, x2: bounds.x2, y2: bounds.y2 }
        const collidesAt: typeof isCollisionAt = !allowEscapeFromOverlap ? isCollisionAt : (x, y) => {
            if (!isCollisionAt(x, y)) return false
            const left = x * TileSize, top = y * TileSize
            const overlaps = start.x1 < left + TileSize && start.x2 > left &&
                start.y1 < top + TileSize && start.y2 > top
            // A projectile can start inside the full-cell wall box while its shooter
            // stands legally beneath the raised wall face. Let it move out of that
            // overlap; an approaching shot or one aimed deeper into the wall still hits.
            const away = ((start.x1 + start.x2) / 2 - left - TileSize / 2) * translate.x +
                ((start.y1 + start.y2) / 2 - top - TileSize / 2) * translate.y
            return !(overlaps && away > 0)
        }
        if (allowEscapeFromOverlap && (translate.x || translate.y)) {
            // Check the starting cells too: a long server tick can otherwise jump from
            // an embedded muzzle straight past the wall without probing its cell.
            for (let x = Math.max(0, Math.floor(start.x1 / TileSize)); x < Math.ceil(start.x2 / TileSize); x++) {
                for (let y = Math.max(0, Math.floor(start.y1 / TileSize)); y < Math.ceil(start.y2 / TileSize); y++) {
                    if (!collidesAt(x, y)) continue
                    result.collided = true
                    result.collision.x = x
                    result.collision.y = y
                    result.adjust.x = (start.x1 + start.x2) / 2
                    result.adjust.y = (start.y1 + start.y2) / 2
                    const intersection = intersectionAt(x, y)
                    if (intersection) {
                        result.intersection.x = x
                        result.intersection.y = y
                        result.intersection.intersectionType = intersection
                    }
                    return
                }
            }
        }

        // going right
        if (translate.x > 0) {
            const x = clamp(Math.floor((bounds.x2 + translate.x) / TileSize), 0, tilemapMetaProvider.width())
            const y1 = clamp(Math.floor(bounds.y1 / TileSize), 0, tilemapMetaProvider.height())
            const y2 = clamp(Math.floor(bounds.y2 / TileSize), 0, tilemapMetaProvider.height())
            let adjust = undefined
            for (let y = y1; y <= y2; y++) {
                if (x > tilemapMetaProvider.width() - 1 || y > tilemapMetaProvider.height() - 1) {
                    continue
                }
                const collides = collidesAt(
                    x,
                    y,
                    bounds.centerCoordinate.x + translate.x,
                    bounds.centerCoordinate.y + translate.y,
                    "Right",
                )

                if (collides) {
                    blocked = true
                    adjust = x * TileSize - 1
                    result.collision.x = x
                    result.collision.y = y
                }
            }
            if (!blocked) {
                bounds.x1 += translate.x
                bounds.x2 += translate.x
            } else if (allowAdjust) {
                bounds.x2 = adjust
                bounds.x1 = adjust - bounds.width
            }
        }

        // // going left
        if (translate.x < 0) {
            const x = clamp(Math.floor((bounds.x1 + translate.x) / TileSize), 0, tilemapMetaProvider.width())
            const y1 = clamp(Math.floor(bounds.y1 / TileSize), 0, tilemapMetaProvider.height())
            const y2 = clamp(Math.floor(bounds.y2 / TileSize), 0, tilemapMetaProvider.height())
            let adjust = undefined
            for (let y = y1; y <= y2; y++) {
                if (x > tilemapMetaProvider.width() - 1 || y > tilemapMetaProvider.height() - 1) {
                    continue
                }
                if (
                    collidesAt(
                        x,
                        y,
                        bounds.centerCoordinate.x + translate.x,
                        bounds.centerCoordinate.y + translate.y,
                        "Left",
                    )
                ) {
                    blocked = true
                    adjust = x * TileSize + TileSize + 1
                    result.collision.x = x
                    result.collision.y = y
                }
            }
            if (!blocked) {
                bounds.x1 += translate.x
                bounds.x2 += translate.x
            } else if (allowAdjust) {
                bounds.x1 = adjust
                bounds.x2 = adjust + bounds.width
            }
        }

        // going up
        if (translate.y < 0) {
            const x1 = clamp(Math.floor(bounds.x1 / TileSize), 0, tilemapMetaProvider.width())
            const x2 = clamp(Math.floor(bounds.x2 / TileSize), 0, tilemapMetaProvider.width())
            const y = clamp(Math.floor((bounds.y1 + translate.y) / TileSize), 0, tilemapMetaProvider.height())
            let adjust = undefined
            for (let x = x1; x <= x2; x++) {
                if (x > tilemapMetaProvider.width() - 1 || y > tilemapMetaProvider.height() - 1) {
                    continue
                }
                if (
                    collidesAt(
                        x,
                        y,
                        bounds.centerCoordinate.x + translate.x,
                        bounds.centerCoordinate.y + translate.y,
                        "Up",
                    )
                ) {
                    blocked = true
                    adjust = y * TileSize + TileSize + 1
                    result.collision.x = x
                    result.collision.y = y
                }
            }
            if (!blocked) {
                bounds.y1 += translate.y
                bounds.y2 += translate.y
            } else if (allowAdjust) {
                bounds.y1 = adjust
                bounds.y2 = adjust + bounds.height
            }
        }

        // going down
        if (translate.y > 0) {
            const x1 = clamp(Math.floor(bounds.x1 / TileSize), 0, tilemapMetaProvider.height())
            const x2 = clamp(Math.floor(bounds.x2 / TileSize), 0, tilemapMetaProvider.height())
            const y = clamp(Math.floor((bounds.y2 + translate.y) / TileSize), 0, tilemapMetaProvider.height())
            let adjust = undefined
            for (let x = x1; x <= x2; x++) {
                if (x > tilemapMetaProvider.width() - 1 || y > tilemapMetaProvider.height() - 1) {
                    continue
                }
                if (
                    collidesAt(
                        x,
                        y,
                        bounds.centerCoordinate.x + translate.x,
                        bounds.centerCoordinate.y + translate.y,
                        "Down",
                    )
                ) {
                    blocked = true
                    adjust = y * TileSize - 1
                    result.collision.x = x
                    result.collision.y = y
                }
            }
            if (!blocked) {
                bounds.y1 += translate.y
                bounds.y2 += translate.y
            } else if (allowAdjust) {
                bounds.y1 = adjust - bounds.height
                bounds.y2 = adjust
            }
        }

        result.collided = blocked

        const x = bounds.x1 + bounds.width / 2
        const y = bounds.y1 + bounds.height / 2

        // The bounds already contain the accepted movement. Probe the leading edge,
        // using the centre on a stationary axis, or the actual blocking cell on impact.
        const probeX = translate.x > 0 ? bounds.x2 : translate.x < 0 ? bounds.x1 : x
        const probeY = translate.y > 0 ? bounds.y2 : translate.y < 0 ? bounds.y1 : y
        const xx = blocked ? result.collision.x : Math.floor(probeX / TileSize)
        const yy = blocked ? result.collision.y : Math.floor(probeY / TileSize)

        const intersection = intersectionAt(xx, yy)

        if (intersection) {
            result.intersection.x = xx
            result.intersection.y = yy
            result.intersection.intersectionType = intersection
        }

        result.adjust.x = x
        result.adjust.y = y
    }

    return func
}

// Re-export for backward compatibility
export { getCurrentTime, setGlobalTimeFunction }

export class Latch {
    private ts: number
    threshold: number
    private expireCallback?: Function
    singleUse: boolean

    constructor(threshold: number, now?: number, expireCallback?: Function) {
        this.ts = now || 0
        this.threshold = threshold
        this.expireCallback = expireCallback
    }

    /**
     * `now` lets a caller that has ALREADY read the clock hand its reading in rather than paying for
     * another one. Same semantics either way; it exists for hot loops that read the clock anyway
     * (see NpcManager.advanceNpcs, where the latch behind a Thruput was the fourth reading per npc).
     */
    expired = (write: boolean = true, now: number = getCurrentTime()): boolean => {
        if (now - this.ts > this.threshold) {
            if (!this.singleUse && write) {
                this.ts = now
            }
            if (this.expireCallback) {
                this.expireCallback()
            }
            return true
        }

        return false
    }

    update = (threshold: number) => {
        this.threshold = threshold
    }

    reset = () => (this.ts = getCurrentTime())

    clear = () => (this.ts = 0)
}

export type Callback = () => void

export type TextDecoration = "underline" | "disabled"

export type DynamicTextType =
    | "text"
    | "button"
    | "button|dynamic"
    | "horizontalLine"
    | "sprite"
    | "grid"
    | "entity"
    | "container"

export interface DynamicTextMeta {
    text: string
    pause?: number
    link?: string
    context?: any
    clickEventEmitter?: PlayerEvent
    type?: DynamicTextType
    sameLine?: boolean
    textDecorations?: TextDecoration[]
    leftSpacer?: number
    leftMargin?: number
    topMargin?: number
    bottomMargin?: number
    spriteId?: string
    column?: number
    exactLocation?: Coordinate
    sameX?: boolean
    children?: DynamicTextMeta[]
    hoverText?: string[]
    font?: string
    scale?: number
    color?: number
}

// A transfer that should ask "how many?" first. The client opens a small quantity picker inside the
// panel (slider, chips, typed entry) and sends the chosen amount back as `transferQuantity` next to
// the option's sealed transaction, which the server clamps against the stack it actually finds.
export interface QuantityPrompt {
    // The largest amount the picker may offer: the stack, already narrowed to what the receiving
    // inventory can really hold. Never below 2 -- a single item has nothing to pick.
    max: number
    // Verb for the picker's title and confirm button ("Take" / "Give").
    verb: string
}

export interface ItemDetailOption {
    title: string
    onClickContext: any
    quantityPrompt?: QuantityPrompt
}

export type InventorySlotSection = "backpack" | "readied" | "equipped"

export type ItemDropTarget = InventorySlotSection | "trade-target" | "trade-player" | "world"

export interface ItemDetailMeta {
    id?: string
    title: string
    options: ItemDetailOption[]
    // Actions are issued by the server; every item UI uses the same drop destinations.
    itemDropOptions?: Partial<Record<ItemDropTarget, ItemDetailOption>>
    // Sealed actions for replacing a particular occupied inventory slot.
    itemSlotDropOptions?: Record<string, ItemDetailOption>
    spriteId: string
    quantity?: number
    active?: boolean
    activeColor?: number
    hoverText?: string[]
    /**
     * How much of this item's durability is left, 0..1, or absent for anything that cannot wear.
     *
     * A number rather than the `ItemCondition` word, because the client draws a gauge and the word
     * is only its caption: four tiers cannot say the difference between an axe at 30% and one at
     * 5%, and that difference is the whole reason to look. Clients derive the word from the
     * fraction with `Mechanics.item.durability.conditionForPct`, so there is one set of thresholds.
     */
    condition?: number
}

export const SIGIL_FLAG: string = "sigil"

/**
 * The name of a LIMBO room -- the empty, tile-less room an EMBER_ENABLED player waits in instead of
 * standing in the overworld (server/services/limbo_room_service.ts).
 *
 * Shared because the client has to recognise one: a room name is the only thing identifying limbo
 * that already rides to the client (WorldState.roomName), and the client needs the answer to know
 * that a room change is COMING -- which is what lets it hold the screen black across the gap
 * between character creation closing and the camp being built, without blacking out a player who
 * finalizes in the overworld with the flag off and is going nowhere.
 */
export const LIMBO_ROOM_NAME: string = "Limbo"

//
// POSSESSION
//
// A player wearing another entity's body. The player's entity stays the one their client predicts,
// renders and sends input for -- nothing about the netcode changes -- and the VESSEL's body is
// copied onto it: appearance, hit points, speed, equipment, allegiance. The vessel itself is left
// standing where it was but suspended (invisible, unstrikeable, its brain not advanced), and both
// are put back when the possession ends. See campaigns/atoms/possession.ts.
//
// Flags carry it, so any system can ask the question without reaching for the campaign, and so that
// EITHER side can be put back on its own when the other one disappears:
//   - the possessor (a player) holds their own body, and where they left it, under POSSESSION_FLAG
//   - the vessel holds its possessor's entityId under POSSESSED_BY_FLAG, and the presence possession
//     took away from it under POSSESSED_PRESENCE_FLAG
//
export const POSSESSION_FLAG: string = "possession"
export const POSSESSED_BY_FLAG: string = "possessedBy"
export const POSSESSED_PRESENCE_FLAG: string = "possessedPresence"

/** The fields of a body that travel with it when it is possessed and given back when it is released. */
export interface PossessedBody {
    appearance?: Appearance
    spriteId?: string
    spriteScale?: number
    dimensions: Dimensions
    hp: number
    maxHp: number
    speed: number
    maxSpeed: number
    weapon?: Weapon
    inventory?: Inventory
    faction?: Faction
    name?: string
    // Allegiance flags lifted off the vessel (see POSSESSION_DEFAULT_TRANSFER_FLAGS) and, separately,
    // the possessor's own flags that were taken away for the duration. Both are restored verbatim.
    transferredFlags?: Record<string, any>
    suppressedFlags?: Record<string, any>
}

/** What `flags.possession` holds on a player who is wearing somebody else's body. */
export interface PossessionState {
    vesselEntityId: EntityId
    startedTs: number
    // Where the possessor was standing when they left their own body -- where they are put back.
    returnLocation: Coordinate & { roomId: EntityId }
    // The possessor's own body, verbatim.
    body: PossessedBody
    // Which flags were taken from the vessel for the duration. Kept by NAME as well as by value,
    // because a flag the possessor never had still has to be taken off them on release.
    transferFlags: string[]
}

/** What `flags.possessedPresence` holds on a suspended vessel: the presence in the world it gets back. */
export interface PossessedPresence {
    visible: boolean
    strikeable?: boolean
    immoveable?: boolean
    npcSafeUntilTs?: number
}

/** True while this entity is a player wearing somebody else's body. */
export const isPossessing = (entity?: Entity): boolean => !!entity?.flags?.[POSSESSION_FLAG]

/** True while this entity is a body being worn by somebody else -- suspended, not really here. */
export const isPossessed = (entity?: Entity): boolean => !!entity?.flags?.[POSSESSED_BY_FLAG]

/** The body this entity is wearing, if any. */
export const possessedVesselId = (entity?: Entity): EntityId | undefined =>
    (entity?.flags?.[POSSESSION_FLAG] as PossessionState)?.vesselEntityId

/** Who is wearing this body, if anybody. */
export const possessorEntityId = (entity?: Entity): EntityId | undefined => entity?.flags?.[POSSESSED_BY_FLAG]

// INVASION -- "Resurrect as Undead!"
//
// A player whose ember run is over, visiting somebody else's camp wearing one of its wave
// attackers. Possession (above) is the mechanic; an invasion is the VISIT that wraps it: which camp
// was joined, who it belongs to, and the fact that this player is a guest rather than a camper.
//
// It is a flag on the visiting player for the same reason possession is: the state has to survive
// its owner (the global campaign is one object for the whole server, a room can be rebuilt, either
// end can disconnect) and anything at all has to be able to ask "is this player a guest here?"
// without reaching for a campaign. The two most important askers are the host camp's own
// controller -- a visitor must not be counted as holding the camp, nor billed one of its lives --
// and the invasion campaign's revalidating sweep. See campaigns/global/global_ember_invasion.ts.
//
export const INVASION_FLAG: string = "invasion"

/** What `flags.invasion` holds on a player who is visiting somebody else's camp as a hostile. */
export interface InvasionState {
    // The camp being visited, and whose it is. The player is the authority on both, because the
    // room can be rebuilt and the host can leave while the visitor is still standing in it.
    hostRoomId: EntityId
    hostPlayerEntityId: EntityId
    // Where the visitor came from. Kept for the record and for logging -- v1 never sends them back
    // there, every exit is a fresh camp -- so nothing may depend on this room still existing.
    homeRoomId: EntityId
    startedTs: number
    // Set once the visit is being wound up, so the five exits cannot each wind it up again. An
    // invasion ends by building its visitor a fresh camp, which is not an idempotent act.
    ending?: boolean
}

/** True while this entity is a player visiting somebody else's camp as a hostile. */
export const isInvading = (entity?: Entity): boolean => !!entity?.flags?.[INVASION_FLAG]

/**
 * A player's standing posture: whether camps of theirs may be invaded.
 *
 * It lives on the PLAYER and not on the room because **an ember camp does not outlive its player.**
 * A camp is a temporary room, and five seconds after its last player leaves it is scheduled for
 * destruction (Default's ALL_PLAYERS_LEFT_ROOM -> RoomDirectory.scheduleForDestruction); a returning
 * player lands in limbo and makes a NEW one. So consent held only on the room silently lapses the
 * moment somebody closes their tab, and their next camp is closed again with nothing on screen
 * saying so -- which is exactly how this was first found.
 *
 * Player flags persist with the account (AccountManager.save), so a posture set once is still set
 * after a relog. `Room.openToTheDead` remains the value the invasion campaign actually reads: this
 * is the remembered posture, copied onto each new camp as it is built.
 */
export const OPEN_TO_THE_DEAD_FLAG: string = "openToTheDead"

/** Whether this player has left their camps open to the dead. */
export const isOpenToTheDead = (entity?: Entity): boolean => !!entity?.flags?.[OPEN_TO_THE_DEAD_FLAG]

/** The invasion this entity is on, if any. */
export const invasionState = (entity?: Entity): InvasionState | undefined =>
    entity?.flags?.[INVASION_FLAG] as InvasionState | undefined

/**
 * One camp a fallen player could rise in, as the picker draws it.
 *
 * Sent as an INTERVAL FEED to every player who is down in a camp, so the "Resurrect as Undead!"
 * button lights up by itself when a camp becomes available -- a fallen player must not have to press
 * it, be told nothing is open, and guess when to press again. The picker therefore opens with no
 * round trip, because the list is already in hand.
 *
 * Like EMBER_PLAYERS, and for the reason documented there, it is keyed ONLY on the interval and
 * never on "has the list changed": the client throws its copy away on every room change, so a
 * change-keyed feed has nothing to say to a player who has just been teleported.
 *
 * Every field is a snapshot of a moving target -- `secondsToWave` is counting down as it is sent,
 * and the fight it counts against may end first -- so nothing here is a promise. The picker says
 * what it last knew, and the server re-checks on the pick.
 */
export interface EmberInvasionCandidate {
    // The DEFENDING player, which is how a pick names the camp it means. Rooms are not addressable
    // from a client and a camp has no id of its own that the client ever sees.
    entityId: EntityId
    name?: string
    // Which wave that camp is on, 1-based; 0 when its first wave has not landed yet.
    wave?: number
    // Whether a wave is on the field right now. True means the visitor possesses an attacker
    // already fighting; false means they wait out the countdown and take the first one to spawn.
    waveActive?: boolean
    // Roughly how long until the next wave lands, in seconds. Undefined when a wave is already on
    // the field, and never a promise -- the fight it is counting against may end first.
    secondsToWave?: number
}

// The overworld popups. Each one is raised by the INTRO campaign at a moment it picks, and each has
// its own hardcoded position beside the widget it explains (see gui/tutorial.ts). They are the thing
// `Tutorial.suppressed` was added to hold back, so that an ember camp is not interrupted by
// overworld chrome.
export type OverworldTutorialType = "hotbar" | "controlbar" | "map" | "companion"

// The first-run ember camp's CLUES: five small pointer-and-text boxes that name, in order, the
// controls a player has to find to build the camp's first fire. Unlike the popups above they are not
// raised on a client-side guess -- the camp's tutorial director arms them one beat at a time, because
// only it knows which beat is live (see assets/scripts/v2/ember-tutorial.js armClues).
//
// In order: open the pack, switch to crafting, craft the campfire, find it in the pack, select Ready.
//
// "ember-run" belongs to no chain and is not part of the fire lesson at all: it names the RUN KEY on
// the first wave's countdown, pointed at the countdown dial, and is the camp's one bit of teaching
// that happens after the Lord has stopped talking. See campaigns/ember/run_hint.ts.
export const EMBER_CLUE_TYPES = [
    "ember-run",
    "ember-ready",
    "ember-wood",
    "ember-wood-items",
    "ember-wood-space",
    "ember-wood-pack",
    "ember-wood-use",
    "ember-items",
    "ember-planks-items",
    "ember-crafting",
    "ember-planks-recipe",
    "ember-wall-recipe",
    "ember-wall-pack",
    "ember-wall-use",
    "ember-roof-recipe",
    "ember-roof-pack",
    "ember-roof-use",
    "ember-recipe",
    "ember-pack",
    "ember-use",
] as const

export type EmberClueType = (typeof EMBER_CLUE_TYPES)[number]

// The two animated helpers (gui/building_tutorial.ts, gui/forager_tutorial.ts) rather than clues.
export type TutorialType = OverworldTutorialType | EmberClueType | "ember-building-demo" | "ember-roof-demo" | "ember-forager-demo" | "ember-demolition-demo" | "ember-sprint-demo"

export const isEmberClueType = (tutorialType: TutorialType | null | undefined): tutorialType is EmberClueType =>
    !!tutorialType && (EMBER_CLUE_TYPES as readonly string[]).includes(tutorialType)

export const MAX_READIED_COUNT = 6

export interface Soundscape {
    soundtrack?: string
    ambience?: string
}

export interface SubmittedFeedback {
    feedback: string
    email: string
}

export interface DungeonSpawnMeta {
    spawnerEntityId?: EntityId
    roomId: string
    roomJson: any
}

export interface AnimatedFeedbackParams {
    variant?: "default" | "static"
    color?: number
    font?: string
    alignment?: "center"
    duration?: number
    scrollSpeed?: number
    type?: "foot" | "vibrate"
    action?: "remove"
    id?: string
    immediate?: boolean
}

export interface CreateNpcProps {
    preSpawnEffect?: PreSpawnEffect | null
    id?: EntityId
    location: Location
    type: NpcType
    spriteId?: string
    patrolLocations?: Coordinate[]
    patrolPause?: number
    portable?: boolean
    color?: number
    name?: string
    flags?: {}
    colliderRange?: number
    appearance?: Appearance
    hp?: number
    hunger?: number
    speed?: number
    weaponType?: WeaponType
    inventory?: Inventory
    clickable?: boolean
    context?: {}
    strikeable?: boolean
    msUntilExpiration?: number
    expirationTs?: number
    persistable?: boolean
    faction?: Faction
    angle?: number
    quantity?: number
    visible?: boolean
    lightMeta?: LightMeta
    leaderId?: EntityId
    dimensions?: Dimensions
    immoveable?: boolean
}

export interface BuildingPointOptions {
    delete?: boolean
}

export const tileMapIterator = <T>(tileMap: TileMapBase<T>, visitor: (x: number, y: number, t: T) => void) => {
    for (let y = 0; y < tileMap.length; y++) {
        for (let x = 0; x < tileMap[y].length; x++) {
            visitor(x, y, tileMap[y][x])
        }
    }
}

export interface GeographicFeature {
    biome: Biome
    biomeCoordinates: Coordinate[]
    center?: Coordinate
    name: string
    id: string
}

export type MapFeatureType = "cave"

export interface MapFeature {
    name: string
    id: string
    roomId: string
    globalLocation: Location
    type: MapFeatureType
}

export interface Descriptor {
    id: string
    description: string
}

export interface QuestObjective extends Descriptor {
    status?: "pending" | "active" | "complete"
    markerLocation?: Location
}

export interface Quest extends IdAware {
    ownerEntityId: EntityId
    triggerIds?: EntityId[]
    participantEntityIds: EntityId[]
    name: string
    descripton?: string
    icon?: LocationIcon
    objectives?: QuestObjective[]
    createdAt: number
    context?: any
    status: "active" | "complete"
}

export interface QuestsContainer {
    currentQuestId?: string
    activeQuests: Quest[]
    completedQuests: Quest[]
}

/** Server-authorized camp feeling, delivered only in the linked Ember cave. */
export interface EmberAnxietyState {
    /** The home lord has died, even if his body has already been removed. */
    fallen?: boolean
    roomId: EntityId
    value: number
    band: "settled" | "uneasy" | "afraid" | "desperate"
    cause: string
}
export const EMBER_ANXIETY_SENSE_FLAG = "emberAnxietySense"
